import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { SessionExpiredModal, SessionWarningBanner } from '../components/SessionExpiredModal';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import NotificationSettings from '../components/NotificationSettings';
import { MemberHelpGuide } from '../components/HelpGuide';
import '../components/HelpGuide.css';
import './MemberDashboard.css';
import { describeLoanRate } from '../utils/loanMath';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);


// Currency symbol mapping
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', KES: 'KSh', NGN: '₦', ZAR: 'R',
  GHS: '₵', UGX: 'USh', TZS: 'TSh', RWF: 'FRw', ETB: 'Br', EGP: 'E£',
  MAD: 'DH', INR: '₹', PHP: '₱', IDR: 'Rp', MYR: 'RM', SGD: 'S$',
  CAD: 'C$', AUD: 'A$', NZD: 'NZ$', BRL: 'R$', MXN: 'MX$'
};

const getCurrencySymbol = (currencyCode) => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode || '$';
};

const formatCurrency = (amount, currencyCode) => {
  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = (amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${symbol}${formattedAmount}`;
};

import { API } from '../config';

// Stripe Card Setup Form for Auto Top-Up
function CardSetupForm({ memberId, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    try {
      const setupRes = await axios.post(`${API}/members/${memberId}/auto-topup/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { client_secret } = setupRes.data;

      const result = await stripe.confirmCardSetup(client_secret, {
        payment_method: { card: elements.getElement(CardElement) }
      });

      if (result.error) {
        toast.error(result.error.message);
        setSaving(false);
        return;
      }

      const confirmRes = await axios.post(`${API}/members/${memberId}/auto-topup/confirm`, {
        setup_intent_id: result.setupIntent.id
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(confirmRes.data.message);
      onSuccess(confirmRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save card');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="card-setup-form" data-testid="card-setup-form">
      <div className="card-element-wrapper">
        <CardElement options={{
          style: {
            base: { fontSize: '16px', color: '#1E293B', '::placeholder': { color: '#94A3B8' } },
            invalid: { color: '#EF4444' }
          }
        }} />
      </div>
      <div className="card-form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={!stripe || saving} data-testid="save-card-btn">
          {saving ? 'Saving...' : 'Save Card'}
        </button>
      </div>
    </form>
  );
}

// Scroll Date Picker for beneficiary DOB
function ScrollDatePicker({ day, setDay, month, setMonth, year, setYear }) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const currentYear = new Date().getFullYear();
  const days = Array.from({length: 31}, (_, i) => i + 1);
  const years = Array.from({length: 100}, (_, i) => currentYear - i);
  
  return (
    <div style={{ display: 'flex', gap: '8px' }} data-testid="scroll-date-picker">
      <div style={{ flex: 1 }}>
        <label style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', display: 'block' }}>Day</label>
        <select
          value={day}
          onChange={(e) => setDay(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', background: '#fff' }}
          data-testid="dob-day-select"
        >
          <option value="">--</option>
          {days.map(d => <option key={d} value={String(d).padStart(2,'0')}>{d}</option>)}
        </select>
      </div>
      <div style={{ flex: 1.2 }}>
        <label style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', display: 'block' }}>Month</label>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', background: '#fff' }}
          data-testid="dob-month-select"
        >
          <option value="">--</option>
          {months.map((m, i) => <option key={m} value={String(i+1).padStart(2,'0')}>{m}</option>)}
        </select>
      </div>
      <div style={{ flex: 1 }}>
        <label style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px', display: 'block' }}>Year</label>
        <select
          value={year}
          onChange={(e) => setYear(e.target.value)}
          style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', background: '#fff' }}
          data-testid="dob-year-select"
        >
          <option value="">--</option>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
      </div>
    </div>
  );
}

function ClaimsTab({ member, groupId, beneficiaries, setBeneficiaries, claims, setClaims, showBeneficiaryModal, setShowBeneficiaryModal, editingBeneficiary, setEditingBeneficiary, beneficiaryForm, setBeneficiaryForm, beneficiaryDobDay, setBeneficiaryDobDay, beneficiaryDobMonth, setBeneficiaryDobMonth, beneficiaryDobYear, setBeneficiaryDobYear, savingBeneficiary, setSavingBeneficiary, currencyCode, changeRequests, setChangeRequests }) {
  const activeBeneficiaries = beneficiaries.filter(b => b.is_active !== false);
  const inactiveBeneficiaries = beneficiaries.filter(b => b.is_active === false);
  const totalAllocation = activeBeneficiaries.reduce((sum, b) => sum + (b.percentage || 0), 0) - (editingBeneficiary ? (activeBeneficiaries.find(b => b.id === editingBeneficiary)?.percentage || 0) : 0);
  const [showLossClaimModal, setShowLossClaimModal] = useState(false);
  const [lossClaimBeneficiary, setLossClaimBeneficiary] = useState(null);
  const [lossClaimDescription, setLossClaimDescription] = useState('');
  const [lossClaimFiles, setLossClaimFiles] = useState([]);
  const [filingLossClaim, setFilingLossClaim] = useState(false);
  const [completingSetup, setCompletingSetup] = useState(false);
  const [showInfoResponseModal, setShowInfoResponseModal] = useState(false);
  const [infoResponseTarget, setInfoResponseTarget] = useState(null);
  const [infoResponseMessage, setInfoResponseMessage] = useState('');

  const initialSetupComplete = member?.beneficiary_initial_setup_complete || false;
  
  const openAddModal = () => {
    setEditingBeneficiary(null);
    setBeneficiaryForm({ full_name: '', relationship: 'spouse', date_of_birth: '', phone_number: '', email: '', id_number: '', percentage: 0 });
    setBeneficiaryDobDay(''); setBeneficiaryDobMonth(''); setBeneficiaryDobYear('');
    setShowBeneficiaryModal(true);
  };
  
  const openEditModal = (b) => {
    setEditingBeneficiary(b.id);
    const dob = b.date_of_birth ? b.date_of_birth.split('-') : ['','',''];
    setBeneficiaryDobYear(dob[0] || '');
    setBeneficiaryDobMonth(dob[1] || '');
    setBeneficiaryDobDay(dob[2] || '');
    setBeneficiaryForm({
      full_name: b.full_name || '', relationship: b.relationship || 'spouse',
      date_of_birth: b.date_of_birth || '', phone_number: b.phone_number || '',
      email: b.email || '', id_number: b.id_number || '', percentage: b.percentage || 0
    });
    setShowBeneficiaryModal(true);
  };
  
  const handleSaveBeneficiary = async () => {
    if (!beneficiaryForm.full_name.trim()) { toast.error('Full name is required'); return; }
    
    setSavingBeneficiary(true);
    const dob = (beneficiaryDobYear && beneficiaryDobMonth && beneficiaryDobDay) ? `${beneficiaryDobYear}-${beneficiaryDobMonth}-${beneficiaryDobDay}` : '';
    const payload = { ...beneficiaryForm, date_of_birth: dob };
    
    try {
      if (editingBeneficiary) {
        const res = await axios.put(`${API}/beneficiaries/${editingBeneficiary}`, payload);
        if (res.data.requires_approval) {
          toast.success('Edit request submitted for leadership approval');
          setChangeRequests(prev => [res.data.request, ...prev]);
        } else {
          setBeneficiaries(prev => prev.map(b => b.id === editingBeneficiary ? res.data : b));
          toast.success('Beneficiary updated');
        }
      } else {
        const res = await axios.post(`${API}/members/${member.id}/beneficiaries`, payload);
        if (res.data.requires_approval) {
          toast.success('Request submitted for leadership approval');
          setChangeRequests(prev => [res.data.request, ...prev]);
        } else {
          setBeneficiaries(prev => [...prev, res.data]);
          toast.success('Beneficiary added');
        }
      }
      setShowBeneficiaryModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save beneficiary');
    } finally {
      setSavingBeneficiary(false);
    }
  };
  
  const handleDeleteBeneficiary = async (id) => {
    if (!window.confirm('Remove this beneficiary?')) return;
    try {
      const res = await axios.delete(`${API}/beneficiaries/${id}`);
      if (res.data.requires_approval) {
        toast.success('Removal request submitted for leadership approval');
        setChangeRequests(prev => [res.data.request, ...prev]);
      } else {
        setBeneficiaries(prev => prev.filter(b => b.id !== id));
        toast.success('Beneficiary removed');
      }
    } catch (error) {
      toast.error('Failed to remove beneficiary');
    }
  };

  const handleCompleteSetup = async () => {
    if (activeBeneficiaries.length === 0) {
      toast.error('Please add at least one beneficiary before completing setup');
      return;
    }
    setCompletingSetup(true);
    try {
      await axios.post(`${API}/members/${member.id}/beneficiaries/complete-setup`);
      toast.success('Beneficiary setup complete! Future changes will require leadership approval.');
      if (member) member.beneficiary_initial_setup_complete = true;
    } catch (error) {
      toast.error('Failed to complete setup');
    } finally {
      setCompletingSetup(false);
    }
  };

  const handleRespondToInfoRequest = async () => {
    if (!infoResponseTarget || !infoResponseMessage.trim()) return;
    try {
      await axios.post(`${API}/members/${member.id}/beneficiary-requests/${infoResponseTarget.id}/respond`, {
        message: infoResponseMessage,
        document_urls: []
      });
      toast.success('Response submitted. Leadership will review.');
      setChangeRequests(prev => prev.map(r => r.id === infoResponseTarget.id ? { ...r, status: 'pending' } : r));
      setShowInfoResponseModal(false);
      setInfoResponseTarget(null);
      setInfoResponseMessage('');
    } catch (error) {
      toast.error('Failed to submit response');
    }
  };

  const handleFileBeneficiaryLoss = async () => {
    if (!lossClaimBeneficiary) return;
    if (!lossClaimFiles || lossClaimFiles.length === 0) {
      toast.error('Please upload at least one supporting document');
      return;
    }
    setFilingLossClaim(true);
    try {
      const formData = new FormData();
      formData.append('beneficiary_id', lossClaimBeneficiary.id);
      formData.append('description', lossClaimDescription || `Loss of beneficiary: ${lossClaimBeneficiary.full_name}`);
      for (const f of lossClaimFiles) {
        formData.append('files', f);
      }
      const res = await axios.post(`${API}/members/${member.id}/claims/beneficiary-loss`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBeneficiaries(prev => prev.map(b => b.id === lossClaimBeneficiary.id ? { ...b, is_active: false } : b));
      setClaims(prev => [res.data, ...prev]);
      toast.success('Beneficiary loss claim filed. Your group leadership will review it.');
      setShowLossClaimModal(false);
      setLossClaimBeneficiary(null);
      setLossClaimDescription('');
      setLossClaimFiles([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to file claim');
    } finally {
      setFilingLossClaim(false);
    }
  };

  const relationshipLabels = { spouse: 'Spouse', child: 'Child', parent: 'Parent', sibling: 'Sibling', other: 'Other' };
  const statusColors = { pending: '#F59E0B', approved: '#3B82F6', disbursed: '#10B981', rejected: '#EF4444' };
  const claimTypeLabels = { beneficiary_loss: 'Beneficiary Loss', member_death: 'Member Death' };

  return (
    <div className="tab-content" data-testid="claims-tab">
      {/* Initial Setup Banner */}
      {!initialSetupComplete && (
        <div data-testid="beneficiary-setup-banner" style={{
          background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
          border: '1px solid #93C5FD', borderRadius: '12px', padding: '20px', marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: '18px', fontWeight: '700' }}>!</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#1E40AF' }}>Complete Your Beneficiary Setup</h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#1E40AF', lineHeight: '1.5' }}>
                Add your beneficiaries now. During this initial setup period, changes are applied immediately.
                Once you complete setup, any future modifications will require leadership approval.
              </p>
              {activeBeneficiaries.length > 0 && (
                <button onClick={handleCompleteSetup} disabled={completingSetup}
                  data-testid="complete-setup-btn"
                  style={{
                    marginTop: '12px', padding: '8px 20px', borderRadius: '8px', border: 'none',
                    background: '#1D4ED8', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                  }}>
                  {completingSetup ? 'Completing...' : 'Complete Setup'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pending Change Requests */}
      {changeRequests && changeRequests.filter(r => r.status === 'pending' || r.status === 'info_requested').length > 0 && (
        <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="pending-requests-section">
          <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1E293B' }}>
            Pending Requests
            <span style={{ marginLeft: '8px', background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
              {changeRequests.filter(r => r.status === 'pending' || r.status === 'info_requested').length}
            </span>
          </h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            {changeRequests.filter(r => r.status === 'pending' || r.status === 'info_requested').map(req => (
              <div key={req.id} data-testid={`change-request-${req.id}`} style={{
                padding: '14px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#FAFAFA'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                      background: req.action === 'add' ? '#DCFCE7' : req.action === 'edit' ? '#DBEAFE' : '#FEE2E2',
                      color: req.action === 'add' ? '#166534' : req.action === 'edit' ? '#1E40AF' : '#991B1B',
                      textTransform: 'capitalize'
                    }}>{req.action}</span>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#1E293B' }}>
                      {req.beneficiary_data?.full_name || 'Beneficiary'}
                    </span>
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                    background: req.status === 'info_requested' ? '#DBEAFE' : '#FEF3C7',
                    color: req.status === 'info_requested' ? '#1E40AF' : '#92400E'
                  }}>
                    {req.status === 'info_requested' ? 'Info Requested' : 'Pending'}
                  </span>
                </div>
                {req.beneficiary_data?.relationship && (
                  <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280' }}>
                    {req.beneficiary_data.relationship} {req.beneficiary_data.percentage ? `| ${req.beneficiary_data.percentage}%` : ''}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: '12px', color: '#9CA3AF' }}>
                  Submitted: {new Date(req.created_at).toLocaleDateString()}
                </p>
                {/* Info Request from leadership */}
                {req.status === 'info_requested' && req.info_requests?.length > 0 && (
                  <div style={{ marginTop: '10px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px', padding: '10px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '600', color: '#92400E' }}>Leadership needs more information:</p>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#78350F' }}>
                      {req.info_requests[req.info_requests.length - 1]?.message || 'Please provide additional details'}
                    </p>
                    {req.info_requests[req.info_requests.length - 1]?.document_types_requested?.length > 0 && (
                      <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#92400E' }}>
                        Requested documents: {req.info_requests[req.info_requests.length - 1].document_types_requested.join(', ')}
                      </p>
                    )}
                    <button onClick={() => { setInfoResponseTarget(req); setInfoResponseMessage(''); setShowInfoResponseModal(true); }}
                      data-testid={`respond-info-request-${req.id}`}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', border: 'none',
                        background: '#D97706', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                      }}>
                      Respond
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Beneficiaries Section */}
      <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#1E293B' }}>My Beneficiaries</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>
              Active allocation: {totalAllocation}% of 100%
              {initialSetupComplete && <span style={{ marginLeft: '8px', color: '#3B82F6' }}> | Changes require approval</span>}
            </p>
          </div>
          <button className="btn btn-primary" onClick={openAddModal} data-testid="add-beneficiary-btn" style={{ padding: '8px 16px', fontSize: '14px' }}>
            + Add Beneficiary
          </button>
        </div>
        
        {activeBeneficiaries.length === 0 && inactiveBeneficiaries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
            <p style={{ fontSize: '16px', margin: '8px 0' }}>No beneficiaries added yet</p>
            <p style={{ fontSize: '13px' }}>Add your beneficiaries to ensure you're compensated in case of their loss</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {activeBeneficiaries.map(b => (
              <div key={b.id} data-testid={`beneficiary-card-${b.id}`} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 18px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#FAFAFA'
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: '600', color: '#1E293B', fontSize: '15px' }}>{b.full_name}</p>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
                    {relationshipLabels[b.relationship] || b.relationship} &middot; {b.percentage}% allocation
                    {b.id_number && <span> &middot; ID: {b.id_number}</span>}
                    {b.date_of_birth && <span> &middot; DOB: {b.date_of_birth}</span>}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button onClick={() => { setLossClaimBeneficiary(b); setLossClaimDescription(''); setLossClaimFiles([]); setShowLossClaimModal(true); }} style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', padding: '4px 10px', color: '#92400E', fontWeight: '600' }} data-testid={`file-loss-claim-${b.id}`} title="File a beneficiary loss claim">
                    File Claim
                  </button>
                  <button onClick={() => openEditModal(b)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px' }} data-testid={`edit-beneficiary-${b.id}`}>✏️</button>
                  <button onClick={() => handleDeleteBeneficiary(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '4px' }} data-testid={`delete-beneficiary-${b.id}`}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Inactive Beneficiaries */}
        {inactiveBeneficiaries.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ fontSize: '15px', color: '#9CA3AF', marginBottom: '10px', borderTop: '1px solid #E5E7EB', paddingTop: '16px' }}>
              Inactive Beneficiaries (Claim Filed)
            </h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              {inactiveBeneficiaries.map(b => (
                <div key={b.id} data-testid={`inactive-beneficiary-${b.id}`} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 18px', borderRadius: '10px', border: '1px dashed #D1D5DB', background: '#F9FAFB', opacity: 0.6
                }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', color: '#9CA3AF', fontSize: '14px', textDecoration: 'line-through' }}>{b.full_name}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#D1D5DB' }}>
                      {relationshipLabels[b.relationship] || b.relationship} &middot; {b.percentage}% allocation
                    </p>
                  </div>
                  <span style={{ background: '#F3F4F6', color: '#9CA3AF', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>
                    INACTIVE
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Claims History */}
      <div className="card" style={{ padding: '24px' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1E293B' }}>Claims History</h2>
        {claims.length === 0 ? (
          <p style={{ color: '#9CA3AF', textAlign: 'center', padding: '30px 0' }}>No claims on record</p>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {claims.map(c => (
              <div key={c.id} style={{ padding: '14px 18px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '11px', background: c.claim_type === 'beneficiary_loss' ? '#EDE9FE' : '#FEE2E2', color: c.claim_type === 'beneficiary_loss' ? '#6D28D9' : '#DC2626', padding: '2px 8px', borderRadius: '4px', fontWeight: '600', marginRight: '8px' }}>
                      {claimTypeLabels[c.claim_type] || c.claim_type || 'Legacy'}
                    </span>
                    <span style={{ fontWeight: '600', color: '#1E293B' }}>
                      {c.claim_type === 'beneficiary_loss' ? c.deceased_beneficiary?.full_name || 'Beneficiary' : c.reason?.replace('_',' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                  <span style={{ background: statusColors[c.status] || '#6B7280', color: '#fff', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                    {c.status?.toUpperCase()}
                  </span>
                </div>
                {c.description && <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>{c.description}</p>}
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
                  Filed: {new Date(c.created_at).toLocaleDateString()} by {c.triggered_by_name}
                  {c.supporting_documents?.length > 0 && <span> &middot; {c.supporting_documents.length} document(s) attached</span>}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Beneficiary Loss Claim Modal */}
      {showLossClaimModal && lossClaimBeneficiary && (
        <div className="modal-overlay" onClick={() => setShowLossClaimModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>File Beneficiary Loss Claim</h2>
              <button className="modal-close" onClick={() => setShowLossClaimModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#92400E' }}>
                Filing a loss claim will mark <strong>{lossClaimBeneficiary.full_name}</strong> as inactive. 
                You will be compensated according to your group's constitution. This action cannot be undone.
              </div>
              <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <p style={{ margin: 0, fontWeight: '600', color: '#1E293B' }}>{lossClaimBeneficiary.full_name}</p>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6B7280' }}>
                  {relationshipLabels[lossClaimBeneficiary.relationship] || lossClaimBeneficiary.relationship} &middot; {lossClaimBeneficiary.percentage}% allocation
                </p>
              </div>
              <div className="form-group">
                <label>Description / Notes</label>
                <textarea value={lossClaimDescription} onChange={(e) => setLossClaimDescription(e.target.value)} placeholder="Additional details about the loss..." rows="3" data-testid="loss-claim-description-input" />
              </div>
              <div className="form-group">
                <label>Supporting Documents * <span style={{ fontSize: '12px', fontWeight: '400', color: '#6B7280' }}>(Required)</span></label>
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px' }}>
                  Upload death certificate, burial permit, or other supporting documents
                </p>
                <input 
                  type="file" 
                  multiple 
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => setLossClaimFiles(Array.from(e.target.files))}
                  data-testid="loss-claim-file-input"
                  style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                />
                {lossClaimFiles.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    {lossClaimFiles.map((f, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', fontSize: '13px', color: '#374151' }}>
                        <span style={{ color: '#10B981' }}>✓</span> {f.name} <span style={{ color: '#9CA3AF' }}>({(f.size / 1024).toFixed(1)} KB)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => setShowLossClaimModal(false)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleFileBeneficiaryLoss} disabled={filingLossClaim} style={{ flex: 1 }} data-testid="confirm-loss-claim-btn">
                  {filingLossClaim ? 'Filing...' : 'File Claim'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Add/Edit Beneficiary Modal */}
      {showBeneficiaryModal && (
        <div className="modal-overlay" onClick={() => setShowBeneficiaryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>{editingBeneficiary ? 'Edit Beneficiary' : 'Add Beneficiary'}</h2>
              <button className="modal-close" onClick={() => setShowBeneficiaryModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" value={beneficiaryForm.full_name} onChange={(e) => setBeneficiaryForm({...beneficiaryForm, full_name: e.target.value})} placeholder="Enter full name" data-testid="beneficiary-name-input" />
              </div>
              <div className="form-group">
                <label>Relationship *</label>
                <select value={beneficiaryForm.relationship} onChange={(e) => setBeneficiaryForm({...beneficiaryForm, relationship: e.target.value})} data-testid="beneficiary-relationship-select">
                  <option value="spouse">Spouse</option>
                  <option value="child">Child</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <ScrollDatePicker
                  day={beneficiaryDobDay} setDay={setBeneficiaryDobDay}
                  month={beneficiaryDobMonth} setMonth={setBeneficiaryDobMonth}
                  year={beneficiaryDobYear} setYear={setBeneficiaryDobYear}
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" value={beneficiaryForm.phone_number} onChange={(e) => setBeneficiaryForm({...beneficiaryForm, phone_number: e.target.value})} placeholder="+254..." data-testid="beneficiary-phone-input" />
              </div>
              <div className="form-group">
                <label>Email <span style={{ fontSize: '12px', fontWeight: '400', color: '#9CA3AF' }}>(Optional)</span></label>
                <input type="email" value={beneficiaryForm.email} onChange={(e) => setBeneficiaryForm({...beneficiaryForm, email: e.target.value})} placeholder="email@example.com" data-testid="beneficiary-email-input" />
              </div>
              <div className="form-group">
                <label>ID / Passport Number (Optional)</label>
                <input type="text" value={beneficiaryForm.id_number} onChange={(e) => setBeneficiaryForm({...beneficiaryForm, id_number: e.target.value})} placeholder="National ID or Passport Number" data-testid="beneficiary-id-input" />
              </div>
              <div className="form-group">
                <label>Allocation Percentage (%)</label>
                <input type="number" min="0" max={100 - totalAllocation + (editingBeneficiary ? (activeBeneficiaries.find(b2 => b2.id === editingBeneficiary)?.percentage || 0) : 0)} value={beneficiaryForm.percentage} onChange={(e) => setBeneficiaryForm({...beneficiaryForm, percentage: parseFloat(e.target.value) || 0})} data-testid="beneficiary-percentage-input" />
                <small style={{ color: '#6B7280' }}>Available: {100 - totalAllocation + (editingBeneficiary ? (activeBeneficiaries.find(b2 => b2.id === editingBeneficiary)?.percentage || 0) : 0)}%</small>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => setShowBeneficiaryModal(false)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveBeneficiary} disabled={savingBeneficiary} style={{ flex: 1 }} data-testid="save-beneficiary-btn">
                  {savingBeneficiary ? 'Saving...' : (editingBeneficiary ? 'Update' : 'Add Beneficiary')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Response Modal */}
      {showInfoResponseModal && infoResponseTarget && (
        <div className="modal-overlay" onClick={() => setShowInfoResponseModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>Respond to Information Request</h2>
              <button className="modal-close" onClick={() => setShowInfoResponseModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#78350F' }}>
                <strong>Request from leadership:</strong><br/>
                {infoResponseTarget.info_requests?.[infoResponseTarget.info_requests.length - 1]?.message || 'Additional information needed'}
              </div>
              <div className="form-group">
                <label>Your Response *</label>
                <textarea value={infoResponseMessage} onChange={e => setInfoResponseMessage(e.target.value)}
                  rows={4} placeholder="Provide the requested information..."
                  data-testid="info-response-message-input"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => setShowInfoResponseModal(false)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleRespondToInfoRequest} disabled={!infoResponseMessage.trim()} style={{ flex: 1 }} data-testid="submit-info-response-btn">
                  Submit Response
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completed Change Requests History */}
      {changeRequests && changeRequests.filter(r => r.status === 'approved' || r.status === 'rejected').length > 0 && (
        <div className="card" style={{ padding: '20px', marginTop: '20px' }} data-testid="completed-requests-section">
          <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1E293B' }}>Request History</h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {changeRequests.filter(r => r.status === 'approved' || r.status === 'rejected').slice(0, 10).map(req => (
              <div key={req.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', borderRadius: '8px', border: '1px solid #E5E7EB', background: '#FAFAFA'
              }}>
                <div>
                  <span style={{
                    padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '600', marginRight: '6px',
                    background: req.action === 'add' ? '#DCFCE7' : req.action === 'edit' ? '#DBEAFE' : '#FEE2E2',
                    color: req.action === 'add' ? '#166534' : req.action === 'edit' ? '#1E40AF' : '#991B1B',
                    textTransform: 'capitalize'
                  }}>{req.action}</span>
                  <span style={{ fontSize: '13px', color: '#374151' }}>{req.beneficiary_data?.full_name || 'Beneficiary'}</span>
                  <span style={{ fontSize: '12px', color: '#9CA3AF', marginLeft: '8px' }}>{new Date(req.created_at).toLocaleDateString()}</span>
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                  background: req.status === 'approved' ? '#DCFCE7' : '#FEE2E2',
                  color: req.status === 'approved' ? '#166534' : '#991B1B',
                  textTransform: 'capitalize'
                }}>{req.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlternativePaymentInfo({ groupId, caseNumbers = [], hasBankConnection = false }) {
  const [instructions, setInstructions] = useState(null);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/groups/${groupId}/payment-instructions`);
        if (res.data && (res.data.zelle_email || res.data.cashapp_tag)) {
          setInstructions(res.data);
        }
      } catch (e) { /* no instructions set */ }
    };
    if (groupId) load();
  }, [groupId]);
  
  const isActive = hasBankConnection && instructions;
  
  return (
    <div data-testid="alt-payment-info" style={{ marginTop: '16px', border: `1px solid ${isActive ? '#E5E7EB' : '#E5E7EB'}`, borderRadius: '12px', overflow: 'hidden', opacity: isActive ? 1 : 0.65 }}>
      <button onClick={() => setExpanded(!expanded)} data-testid="toggle-alt-payment"
        style={{ width: '100%', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isActive ? '#F9FAFB' : '#F3F4F6', border: 'none', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: isActive ? '#1E293B' : '#9CA3AF' }}>Pay with Zelle or Cash App</span>
          {!isActive && (
            <span style={{ fontSize: '11px', padding: '2px 8px', background: '#F3F4F6', border: '1px solid #D1D5DB', borderRadius: '20px', color: '#6B7280' }}>
              {!hasBankConnection ? 'Pending bank link' : 'Not configured'}
            </span>
          )}
        </div>
        <span style={{ color: '#6B7280', fontSize: '18px' }}>{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div style={{ padding: '16px' }}>
          {!hasBankConnection ? (
            <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#92400E', fontWeight: '500' }}>
                Your group leadership has not yet linked a bank account. Zelle and Cash App payment options will be available once a bank account is connected.
              </p>
            </div>
          ) : !instructions ? (
            <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#0369A1', fontWeight: '500' }}>
                Bank account is linked but payment instructions have not been configured yet. Ask your leadership to set up Zelle/Cash App details.
              </p>
            </div>
          ) : (
            <>
              {caseNumbers.length > 0 && (
                <div style={{ background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: '8px', padding: '12px', marginBottom: '14px' }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1E40AF' }}>
                    Include your case reference in the payment memo:
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {caseNumbers.map(cn => (
                      <span key={cn} style={{ padding: '4px 10px', background: '#1D4ED8', color: '#fff', borderRadius: '6px', fontSize: '13px', fontWeight: '700', letterSpacing: '0.5px' }}>
                        {cn}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {(instructions.zelle_email || instructions.zelle_phone) && (
                  <div style={{ padding: '14px', borderRadius: '10px', background: '#EDE9FE', border: '1px solid #C4B5FD' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#5B21B6' }}>Zelle</p>
                    {instructions.zelle_name && <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#6D28D9' }}>To: {instructions.zelle_name}</p>}
                    <p style={{ margin: 0, fontSize: '15px', color: '#4C1D95', fontWeight: '700' }}>
                      {instructions.zelle_email || instructions.zelle_phone}
                    </p>
                  </div>
                )}
                {instructions.cashapp_tag && (
                  <div style={{ padding: '14px', borderRadius: '10px', background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: '700', color: '#065F46' }}>Cash App</p>
                    {instructions.cashapp_name && <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#047857' }}>To: {instructions.cashapp_name}</p>}
                    <p style={{ margin: 0, fontSize: '15px', color: '#064E3B', fontWeight: '700' }}>{instructions.cashapp_tag}</p>
                  </div>
                )}
              </div>
              {instructions.additional_instructions && (
                <p style={{ marginTop: '10px', padding: '10px', background: '#FEF3C7', borderRadius: '8px', fontSize: '13px', color: '#78350F' }}>
                  {instructions.additional_instructions}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function MemberDashboard({ user }) {
  const { settings: platformSettings } = usePlatformSettings();
  const { sessionExpired, showWarning, handleSessionLogout, dismissWarning } = useInactivityTimeout();
  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [member, setMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [probationStatus, setProbationStatus] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [paymentNotifications, setPaymentNotifications] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [caseNumbers, setCaseNumbers] = useState([]);
  const [filterCaseNumber, setFilterCaseNumber] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadCaseNumber, setUploadCaseNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [myProofs, setMyProofs] = useState([]);
  // Pool payment state
  const [isPoolPayment, setIsPoolPayment] = useState(false);
  const [poolMembers, setPoolMembers] = useState([]);
  const [poolMemberInput, setPoolMemberInput] = useState('');
  const [poolAmount, setPoolAmount] = useState('');
  const [lookingUpMember, setLookingUpMember] = useState(false);
  // Wallet & Autopay state
  const [wallet, setWallet] = useState({ wallet_balance: 0, autopay_enabled: false, prepaid_active: false, cases_covered: 0 });
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [walletTransactions, setWalletTransactions] = useState([]);
  // M-Pesa state
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [mpesaCaseNumber, setMpesaCaseNumber] = useState('');
  const [mpesaSending, setMpesaSending] = useState(false);
  const [mpesaStatus, setMpesaStatus] = useState(null); // null | 'sent' | 'completed' | 'failed'
  const [mpesaCheckoutId, setMpesaCheckoutId] = useState('');
  const [mpesaTxnId, setMpesaTxnId] = useState('');
  const [walletSubTab, setWalletSubTab] = useState('balance');
  const [prepaidTransactions, setPrepaidTransactions] = useState([]);
  const [showCardSetup, setShowCardSetup] = useState(false);
  const [autoTopUpAmount, setAutoTopUpAmount] = useState('');
  const [autoTopUpThreshold, setAutoTopUpThreshold] = useState(3);
  const [savingAutoTopUp, setSavingAutoTopUp] = useState(false);
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  // Support Ticket state
  const [supportTickets, setSupportTickets] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketCategory, setTicketCategory] = useState('general');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [submittingTicket, setSubmittingTicket] = useState(false);
  // Payment dropdown state for overview
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  // Claims & Beneficiary state
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [claims, setClaims] = useState([]);
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const [beneficiaryForm, setBeneficiaryForm] = useState({
    full_name: '', relationship: 'spouse', date_of_birth: '',
    phone_number: '', email: '', id_number: '', percentage: 100
  });
  const [beneficiaryDobDay, setBeneficiaryDobDay] = useState('');
  const [beneficiaryDobMonth, setBeneficiaryDobMonth] = useState('');
  const [beneficiaryDobYear, setBeneficiaryDobYear] = useState('');
  const [savingBeneficiary, setSavingBeneficiary] = useState(false);
  const [beneficiaryChangeRequests, setBeneficiaryChangeRequests] = useState([]);
  // Loans state
  const [memberLoans, setMemberLoans] = useState([]);
  const [showLoanRequestModal, setShowLoanRequestModal] = useState(false);
  const [loanRequestForm, setLoanRequestForm] = useState({ amount: '', purpose: '', term_months: '', physical_address: '', mailing_address: '', sameAddress: false });
  const [submittingLoanRequest, setSubmittingLoanRequest] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [groupId]);

  const loadData = async () => {
    try {
      const [groupRes, membersRes, paymentsRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/groups/${groupId}/members`),
        axios.get(`${API}/groups/${groupId}/payments`),
        axios.get(`${API}/groups/${groupId}/meetings`)
      ]);

      setGroup(groupRes.data);
      
      // Find current user's member record
      const currentMember = membersRes.data.find(m => m.user_id === user.id);
      setMember(currentMember);

      // Filter payments for current member
      const memberPayments = paymentsRes.data.filter(p => p.member_id === currentMember?.id);
      setPayments(memberPayments);
      
      // Check probation status
      if (currentMember && currentMember.status === 'probation') {
        try {
          const probationRes = await axios.get(`${API}/members/${currentMember.id}/probation-status`);
          setProbationStatus(probationRes.data);
        } catch (error) {
          console.error('Failed to load probation status');
        }
      }
      
      // Fetch notifications
      if (currentMember) {
        try {
          const notificationsRes = await axios.get(`${API}/members/${currentMember.id}/notifications`);
          setNotifications(notificationsRes.data);
          
          // Fetch payment notifications separately
          const paymentNotifRes = await axios.get(`${API}/members/${currentMember.id}/payment-notifications`);
          setPaymentNotifications(paymentNotifRes.data);
          
          // Fetch broadcasts (meetings + general broadcasts)
          const broadcastsRes = await axios.get(`${API}/members/${currentMember.id}/broadcasts`);
          setBroadcasts(broadcastsRes.data);
          
          // Fetch member's proof of payments
          const proofsRes = await axios.get(`${API}/members/${currentMember.id}/proof-of-payment?group_id=${groupId}`);
          setMyProofs(proofsRes.data);
          
          // Fetch wallet data
          const walletRes = await axios.get(`${API}/members/${currentMember.id}/wallet`);
          setWallet(walletRes.data);
          if (walletRes.data.auto_topup_amount) setAutoTopUpAmount(String(walletRes.data.auto_topup_amount));
          if (walletRes.data.auto_topup_threshold_cases) setAutoTopUpThreshold(walletRes.data.auto_topup_threshold_cases);
          
          // Fetch wallet transactions
          const transactionsRes = await axios.get(`${API}/members/${currentMember.id}/wallet/transactions`);
          setWalletTransactions(transactionsRes.data);
          
          // Fetch prepaid-specific transactions
          const prepaidTxRes = await axios.get(`${API}/members/${currentMember.id}/wallet/transactions?filter=prepaid`);
          setPrepaidTransactions(prepaidTxRes.data);
          
          // Fetch beneficiaries and claims
          const beneficiariesRes = await axios.get(`${API}/members/${currentMember.id}/beneficiaries`);
          setBeneficiaries(beneficiariesRes.data);
          
          const claimsRes = await axios.get(`${API}/members/${currentMember.id}/claims?filed_by_me=true`);
          setClaims(claimsRes.data);
          
          // Load beneficiary change requests
          try {
            const changeReqRes = await axios.get(`${API}/members/${currentMember.id}/beneficiary-requests`);
            setBeneficiaryChangeRequests(changeReqRes.data);
          } catch (e) { /* no requests yet */ }
          
          // Load member loans (non-benevolence groups)
          try {
            const loansRes = await axios.get(`${API}/members/${currentMember.id}/loans`);
            setMemberLoans(loansRes.data);
          } catch (e) { /* benevolence groups will 403 - that's OK */ }
        } catch (error) {
          console.error('Failed to load notifications');
        }
      }
      
      // Extract unique case numbers from payments
      const uniqueCaseNumbers = [...new Set(memberPayments.map(p => p.case_number).filter(Boolean))];
      setCaseNumbers(uniqueCaseNumbers);
      setFilteredPayments(memberPayments);

      setMeetings(meetingsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to payments
  useEffect(() => {
    let filtered = [...payments];

    // Filter by case number
    if (filterCaseNumber) {
      filtered = filtered.filter(p => p.case_number === filterCaseNumber);
    }

    // Filter by date range
    if (filterStartDate) {
      filtered = filtered.filter(p => new Date(p.date) >= new Date(filterStartDate));
    }
    if (filterEndDate) {
      filtered = filtered.filter(p => new Date(p.date) <= new Date(filterEndDate));
    }

    setFilteredPayments(filtered);
  }, [filterCaseNumber, filterStartDate, filterEndDate, payments]);

  const clearFilters = () => {
    setFilterCaseNumber('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  // Loan request functions
  const handleSubmitLoanRequest = async () => {
    if (!loanRequestForm.amount || !loanRequestForm.purpose || !loanRequestForm.term_months) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmittingLoanRequest(true);
    try {
      await axios.post(`${API}/members/${member.id}/loan-request`, {
        amount: parseFloat(loanRequestForm.amount),
        purpose: loanRequestForm.purpose,
        term_months: parseInt(loanRequestForm.term_months),
        physical_address: loanRequestForm.physical_address || null,
        mailing_address: loanRequestForm.sameAddress ? loanRequestForm.physical_address : (loanRequestForm.mailing_address || null)
      });
      toast.success('Loan request submitted successfully');
      setShowLoanRequestModal(false);
      setLoanRequestForm({ amount: '', purpose: '', term_months: '', physical_address: '', mailing_address: '', sameAddress: false });
      const loansRes = await axios.get(`${API}/members/${member.id}/loans`);
      setMemberLoans(loansRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit loan request');
    } finally {
      setSubmittingLoanRequest(false);
    }
  };

  const handleAcceptLoan = async (loanId) => {
    try {
      await axios.put(`${API}/members/${member.id}/loans/${loanId}/accept`);
      toast.success('Loan terms accepted and signed');
      setSelectedLoan(null);
      const loansRes = await axios.get(`${API}/members/${member.id}/loans`);
      setMemberLoans(loansRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept loan');
    }
  };

  const handleDeclineLoan = async (loanId) => {
    try {
      await axios.put(`${API}/members/${member.id}/loans/${loanId}/decline`);
      toast.success('Loan offer declined');
      setSelectedLoan(null);
      const loansRes = await axios.get(`${API}/members/${member.id}/loans`);
      setMemberLoans(loansRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline loan');
    }
  };

  const handleLoanRepayment = async (loan) => {
    try {
      const res = await axios.post(`${API}/members/${member.id}/loans/${loan.id}/checkout`);
      if (res.data.url) {
        // Store loan context for after payment
        localStorage.setItem('pending_loan_repayment', JSON.stringify({
          loan_id: loan.id,
          member_id: member.id,
          session_id: res.data.session_id
        }));
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
    }
  };

  // Load support tickets
  const loadSupportTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/support/tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Filter to current group only
      const groupTickets = response.data.filter(t => t.group_id === groupId);
      setSupportTickets(groupTickets);
    } catch (error) {
      console.error('Failed to load support tickets:', error);
    }
  };

  // Submit new support ticket
  const handleSubmitTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    setSubmittingTicket(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/support/tickets`, {
        subject: ticketSubject,
        message: ticketMessage,
        category: ticketCategory,
        group_id: groupId
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`Ticket ${response.data.ticket_number} created successfully!`);
      setShowTicketModal(false);
      setTicketSubject('');
      setTicketMessage('');
      setTicketCategory('general');
      loadSupportTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  // Submit reply to ticket
  const handleSubmitReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, {
        message: ticketReply
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Reply sent successfully!');
      setTicketReply('');
      loadSupportTickets();
      // Refresh selected ticket
      const updatedTicket = await axios.get(`${API}/support/tickets/${selectedTicket.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedTicket(updatedTicket.data);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  // Load tickets when support tab is active
  useEffect(() => {
    if (activeTab === 'support') {
      loadSupportTickets();
    }
  }, [activeTab]);

  const handlePayment = async (caseNumber = null) => {
    if (!member) {
      toast.error('Member record not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Use Connect checkout if group has connected account
      const endpoint = group.connected_account_id && group.connected_account_status === 'active'
        ? `${API}/payments/checkout-connect`
        : `${API}/payments/checkout`;
      
      const requestData = {
        member_id: member.id,
        group_id: groupId
      };
      
      // Add case number if provided
      if (caseNumber) {
        requestData.case_number = caseNumber;
      }
      
      const response = await axios.post(endpoint, requestData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'origin': window.location.origin
        }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  // M-Pesa STK Push handler
  const handleMpesaPayment = async () => {
    if (!mpesaPhone || mpesaPhone.length < 9) {
      toast.error('Enter a valid M-Pesa phone number');
      return;
    }
    if (!mpesaCaseNumber) {
      toast.error('Select a case number');
      return;
    }
    setMpesaSending(true);
    setMpesaStatus(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/mpesa/stk-push`, {
        phone_number: mpesaPhone,
        amount: group?.payment_amount || 0,
        group_id: groupId,
        case_id: mpesaCaseNumber,
        member_id: member?.id
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.success) {
        setMpesaStatus('sent');
        setMpesaCheckoutId(res.data.checkout_request_id);
        setMpesaTxnId(res.data.transaction_id);
        toast.success(res.data.message || 'Check your phone for M-Pesa prompt');
        // Start polling for status
        pollMpesaStatus(res.data.checkout_request_id);
      } else {
        setMpesaStatus('failed');
        toast.error(res.data.message || 'STK Push failed');
      }
    } catch (err) {
      setMpesaStatus('failed');
      toast.error(err.response?.data?.detail || 'M-Pesa payment failed');
    } finally {
      setMpesaSending(false);
    }
  };

  const pollMpesaStatus = (checkoutId) => {
    let attempts = 0;
    const maxAttempts = 12; // Poll for ~60 seconds
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(interval);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/mpesa/query-status`, {
          checkout_request_id: checkoutId
        }, { headers: { Authorization: `Bearer ${token}` } });

        if (res.data.status === 'completed') {
          setMpesaStatus('completed');
          toast.success('M-Pesa payment confirmed!');
          clearInterval(interval);
        } else if (res.data.status === 'failed' || res.data.status === 'cancelled') {
          setMpesaStatus('failed');
          toast.error(res.data.result_desc || 'Payment was not completed');
          clearInterval(interval);
        }
      } catch (err) {
        // Continue polling on error
      }
    }, 5000);
  };

  const generatePaymentHistoryPDF = () => {
    if (!member || !group || payments.length === 0) {
      toast.error('No payment history available');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;

      // Title
      doc.setFontSize(20);
      doc.setTextColor(39, 174, 96);
      doc.text('Payment History Report', pageWidth / 2, 20, { align: 'center' });

      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });

      // Member Info Section
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text('Member Information', 14, 40);

      const memberInfo = [
        ['Name', member.user_name || 'N/A'],
        ['Email', member.user_email || 'N/A'],
        ['Phone', member.phone_number || 'N/A'],
        ['Group', group.name || 'N/A'],
        ['Member Since', member.join_date ? new Date(member.join_date).toLocaleDateString() : 'N/A'],
        ['Status', member.status || 'N/A'],
        ['Total Paid', formatCurrency(member.total_paid || 0, group?.currency)],
        ['Total Payments', (member.payment_count || 0).toString()]
      ];

      doc.autoTable({
        startY: 45,
        head: [['Field', 'Value']],
        body: memberInfo,
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 14, right: 14 }
      });

      // Payment History Section
      const finalY = (doc.lastAutoTable?.finalY || 100) + 15;
      doc.setFontSize(14);
      doc.text('Payment History', 14, finalY);

      const currencySymbol = getCurrencySymbol(group?.currency);
      const paymentData = payments.map(payment => [
        new Date(payment.date).toLocaleDateString(),
        `${currencySymbol}${payment.amount.toFixed(2)}`,
        payment.status || 'completed'
      ]);

      doc.autoTable({
        startY: finalY + 5,
        head: [['Date', 'Amount', 'Status']],
        body: paymentData,
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 14, right: 14 }
      });

      // Summary Section
      const summaryY = (doc.lastAutoTable?.finalY || 150) + 15;
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Summary', 14, summaryY);

      const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);
      const avgPayment = totalAmount / payments.length;

      const summaryData = [
        ['Total Payments Made', payments.length.toString()],
        ['Total Amount Paid', formatCurrency(totalAmount, group?.currency)],
        ['Average Payment', formatCurrency(avgPayment, group?.currency)],
        ['Last Payment Date', payments.length > 0 ? new Date(payments[0].date).toLocaleDateString() : 'N/A']
      ];

      doc.autoTable({
        startY: summaryY + 5,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'plain',
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 14, right: 14 }
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${pageCount} | ${group.name} - Payment History`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      doc.save(`${member.user_name.replace(/\s+/g, '_')}_Payment_History_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Payment history PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(`Failed to generate PDF: ${error.message}`);
    }
  };

  const handleUploadProof = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    if (isPoolPayment) {
      if (poolMembers.length === 0) {
        toast.error('Please add at least one member to the pool payment');
        return;
      }
      if (!poolAmount || parseFloat(poolAmount) <= 0) {
        toast.error('Please enter the amount shown in the proof');
        return;
      }
      if (!uploadCaseNumber) {
        toast.error('Please select a case number for pool payment');
        return;
      }
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      if (isPoolPayment) {
        const poolMemberIds = poolMembers.map(pm => pm.member_id).join(',');
        const params = new URLSearchParams();
        params.append('group_id', groupId);
        params.append('case_number', uploadCaseNumber);
        params.append('pool_amount', poolAmount);
        params.append('pool_member_ids', poolMemberIds);
        if (uploadNotes) params.append('notes', uploadNotes);

        await axios.post(
          `${API}/members/${member.id}/pool-payment?${params.toString()}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success(`Pool payment uploaded for ${poolMembers.length + 1} members (including yourself)!`);
      } else {
        const params = new URLSearchParams();
        params.append('group_id', groupId);
        if (uploadCaseNumber) params.append('case_number', uploadCaseNumber);
        if (uploadNotes) params.append('notes', uploadNotes);

        await axios.post(
          `${API}/members/${member.id}/proof-of-payment?${params.toString()}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Proof of payment uploaded successfully! Leadership will review it.');
      }

      setShowUploadModal(false);
      setUploadFile(null);
      setUploadNotes('');
      setUploadCaseNumber('');
      setIsPoolPayment(false);
      setPoolMembers([]);
      setPoolMemberInput('');
      setPoolAmount('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload proof of payment');
    } finally {
      setUploading(false);
    }
  };

  const handleLookupPoolMember = async () => {
    if (!poolMemberInput.trim()) {
      toast.error('Please enter a membership number');
      return;
    }
    if (poolMembers.some(pm => pm.membership_number === poolMemberInput.trim())) {
      toast.error('This member is already added');
      return;
    }
    setLookingUpMember(true);
    try {
      const response = await axios.get(`${API}/groups/${groupId}/members/lookup?membership_number=${encodeURIComponent(poolMemberInput.trim())}`);
      if (response.data.member_id === member.id) {
        toast.error('You cannot add yourself - you are automatically included as the submitter');
        return;
      }
      setPoolMembers(prev => [...prev, response.data]);
      setPoolMemberInput('');
      toast.success(`${response.data.name} added to pool payment`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Member not found in this group');
    } finally {
      setLookingUpMember(false);
    }
  };

  const removePoolMember = (memberId) => {
    setPoolMembers(prev => prev.filter(pm => pm.member_id !== memberId));
  };

  // Delete proof of payment
  const handleDeleteProof = async (proofId) => {
    if (!window.confirm('Are you sure you want to delete this proof of payment?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/proof-of-payment/${proofId}`);
      toast.success('Proof of payment deleted');
      loadData(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete proof of payment');
    }
  };

  // Toggle autopay setting
  const handleToggleAutopay = async () => {
    if (!member) return;
    
    try {
      const newValue = !wallet.autopay_enabled;
      await axios.post(`${API}/members/${member.id}/autopay`, {
        enabled: newValue
      });
      
      setWallet({ ...wallet, autopay_enabled: newValue });
      toast.success(`Autopay ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update autopay setting');
    }
  };

  // Handle wallet top-up
  const handleWalletTopUp = async () => {
    if (!member || !topUpAmount || parseFloat(topUpAmount) < 5) {
      toast.error(`Minimum top-up amount is ${getCurrencySymbol(group?.currency)}5`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/members/${member.id}/wallet/topup`, {
        amount: parseFloat(topUpAmount)
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'origin': window.location.origin
        }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate top-up');
    }
  };

  // Auto top-up handlers
  const handleCardSaved = (data) => {
    setShowCardSetup(false);
    setWallet(prev => ({
      ...prev,
      auto_topup_enabled: true,
      auto_topup_card_last4: data.card_last4,
      auto_topup_card_brand: data.card_brand
    }));
    // Reload wallet data
    if (member) {
      axios.get(`${API}/members/${member.id}/wallet`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(res => setWallet(res.data)).catch(() => {});
    }
  };

  const handleToggleAutoTopUp = async () => {
    if (!member) return;
    const newEnabled = !wallet.auto_topup_enabled;
    if (newEnabled && !wallet.auto_topup_card_last4) {
      setShowCardSetup(true);
      return;
    }
    setSavingAutoTopUp(true);
    try {
      await axios.put(`${API}/members/${member.id}/auto-topup/settings`, {
        enabled: newEnabled,
        topup_amount: wallet.auto_topup_amount || (wallet.contribution_amount || 50) * 3,
        threshold_cases: wallet.auto_topup_threshold_cases || 3
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setWallet(prev => ({ ...prev, auto_topup_enabled: newEnabled }));
      toast.success(`Auto top-up ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update auto top-up');
    }
    setSavingAutoTopUp(false);
  };

  const handleSaveAutoTopUpSettings = async () => {
    if (!member) return;
    const amount = parseFloat(autoTopUpAmount);
    if (!amount || amount < 5) {
      toast.error(`Minimum auto top-up amount is ${getCurrencySymbol(group?.currency)}5`);
      return;
    }
    setSavingAutoTopUp(true);
    try {
      const res = await axios.put(`${API}/members/${member.id}/auto-topup/settings`, {
        enabled: wallet.auto_topup_enabled,
        topup_amount: amount,
        threshold_cases: autoTopUpThreshold
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setWallet(prev => ({
        ...prev,
        auto_topup_amount: amount,
        auto_topup_threshold_cases: autoTopUpThreshold
      }));
      toast.success('Auto top-up settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSavingAutoTopUp(false);
  };

  const handleRemoveCard = async () => {
    if (!member || !window.confirm('Remove saved card and disable auto top-up?')) return;
    try {
      await axios.delete(`${API}/members/${member.id}/auto-topup/card`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setWallet(prev => ({
        ...prev,
        auto_topup_enabled: false,
        auto_topup_card_last4: null,
        auto_topup_card_brand: null,
        auto_topup_payment_method_id: null
      }));
      toast.success('Card removed and auto top-up disabled');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove card');
    }
  };

  // Check for wallet top-up success from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const walletTopup = urlParams.get('wallet_topup');
    const amount = urlParams.get('amount');
    
    if (walletTopup === 'success' && amount) {
      toast.success(`${formatCurrency(parseFloat(amount), group?.currency)} has been added to your prepaid balance!`);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Reload wallet data
      if (member) {
        axios.get(`${API}/members/${member.id}/wallet`)
          .then(res => setWallet(res.data))
          .catch(err => console.error('Failed to refresh wallet'));
      }
    } else if (walletTopup === 'cancelled') {
      toast.info('Top-up was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [member]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="member-dashboard" data-testid="member-dashboard">
      {sessionExpired && <SessionExpiredModal onLogin={handleSessionLogout} />}
      {showWarning && !sessionExpired && <SessionWarningBanner onDismiss={dismissWarning} />}
      <nav className="dashboard-nav">
        <div className="nav-left">
          <div className="logo">
            {platformSettings.platform_logo_url ? (
              <img 
                src={`${platformSettings.platform_logo_url}?v=${Date.now()}`} 
                alt="Logo" 
                className="logo-image"
              />
            ) : (
              <span className="logo-icon">{platformSettings.platform_logo_text || '💰'}</span>
            )}
            <div className="logo-text-container">
              <span className="group-name">{group?.name || platformSettings.platform_name}</span>
              <span className="powered-by">Powered by {platformSettings.platform_name}</span>
            </div>
          </div>
        </div>
        <div className="nav-right">
          {user.role === 'leadership' && (
            <button 
              onClick={() => navigate('/dashboard')} 
              className="btn-switch-view"
              data-testid="switch-to-leadership-btn"
            >
              🔄 Switch to Leadership View
            </button>
          )}
          <button onClick={() => navigate('/group-selection')} className="btn-back">← Back to Groups</button>
          <span className="user-name">👤 {user.name}</span>
          <button onClick={handleLogout} className="btn-logout" data-testid="logout-btn">Logout</button>
        </div>
      </nav>

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>{group?.name}</h1>
          <p>{group?.description}</p>
        </div>

        {/* Tab Navigation */}
        
        {/* Probation Banner */}
        {probationStatus?.on_probation && (
          <div data-testid="probation-banner" style={{
            background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
            border: '1px solid #F59E0B',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: '#F59E0B', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', flexShrink: 0
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: '700', color: '#92400E', fontSize: '15px' }}>
                  You are on probation
                </div>
                <div style={{ color: '#A16207', fontSize: '13px', marginTop: '2px' }}>
                  {probationStatus.reason || 'Contact your group leader for more information'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ textAlign: 'center' }}>
                <div data-testid="probation-days-remaining" style={{ fontSize: '26px', fontWeight: '800', color: '#92400E' }}>
                  {probationStatus.expired ? 0 : probationStatus.days_remaining}
                </div>
                <div style={{ fontSize: '11px', color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  days left
                </div>
              </div>
              {/* Mini progress ring */}
              <div style={{ position: 'relative', width: '44px', height: '44px', flexShrink: 0 }}>
                <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(245,158,11,0.2)" strokeWidth="4" />
                  <circle cx="22" cy="22" r="18" fill="none" stroke="#F59E0B" strokeWidth="4"
                    strokeDasharray={`${((probationStatus.progress_percent || 0) / 100) * 113.1} 113.1`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                  />
                </svg>
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  fontSize: '10px', fontWeight: '700', color: '#92400E'
                }}>
                  {probationStatus.progress_percent || 0}%
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="member-tabs">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            data-testid="tab-overview"
          >
            <span className="tab-icon">🏠</span>
            <span className="tab-text">Overview</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'wallet' ? 'active' : ''}`}
            onClick={() => setActiveTab('wallet')}
            data-testid="tab-wallet"
          >
            <span className="tab-icon">💰</span>
            <span className="tab-text">Wallet</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
            data-testid="tab-payments"
          >
            <span className="tab-icon">💳</span>
            <span className="tab-text">Payments</span>
          </button>
          <button 
            className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
            data-testid="tab-notifications"
          >
            <span className="tab-icon">🔔</span>
            <span className="tab-text">Notifications</span>
            {paymentNotifications.length > 0 && (
              <span className="tab-badge">{paymentNotifications.length}</span>
            )}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'meetings' ? 'active' : ''}`}
            onClick={() => setActiveTab('meetings')}
            data-testid="tab-meetings"
          >
            <span className="tab-icon">📅</span>
            <span className="tab-text">Meetings</span>
            {meetings.length > 0 && (
              <span className="tab-badge">{meetings.length}</span>
            )}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'support' ? 'active' : ''}`}
            onClick={() => setActiveTab('support')}
            data-testid="tab-support"
          >
            <span className="tab-icon">🎫</span>
            <span className="tab-text">Support</span>
            {supportTickets.filter(t => t.status === 'open' || t.status === 'in_progress').length > 0 && (
              <span className="tab-badge">{supportTickets.filter(t => t.status === 'open' || t.status === 'in_progress').length}</span>
            )}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'claims' ? 'active' : ''}`}
            onClick={() => setActiveTab('claims')}
            data-testid="tab-claims"
          >
            <span className="tab-icon">🛡️</span>
            <span className="tab-text">Beneficiaries</span>
          </button>
          {group?.category !== 'benevolence' && (
            <button 
              className={`tab-btn ${activeTab === 'loans' ? 'active' : ''}`}
              onClick={() => setActiveTab('loans')}
              data-testid="tab-loans"
            >
              <span className="tab-icon">🏦</span>
              <span className="tab-text">Loans</span>
              {memberLoans.filter(l => l.status === 'terms_set').length > 0 && (
                <span className="tab-badge">{memberLoans.filter(l => l.status === 'terms_set').length}</span>
              )}
            </button>
          )}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="status-section card">
              <div className="status-header">
                <h2>Your Status</h2>
              </div>
              
              {/* Prominent Member Status Display */}
              <div className={`member-status-card status-${member?.status}`} data-testid="member-status-card">
                <div className="status-icon">
                  {member?.status === 'active' && '✅'}
                  {member?.status === 'probation' && '⚠️'}
                  {member?.status === 'inactive' && '❌'}
                  {member?.status === 'suspended' && '🚫'}
                </div>
                <div className="status-info">
                  <span className="status-title">
                    {member?.status === 'active' && 'Active Member'}
                    {member?.status === 'probation' && 'On Probation'}
                    {member?.status === 'inactive' && 'Inactive'}
                    {member?.status === 'suspended' && 'Suspended'}
                  </span>
                  <span className="status-description">
                    {member?.status === 'active' && 'Your membership is in good standing'}
                    {member?.status === 'probation' && 'Please make payment to restore active status'}
                    {member?.status === 'inactive' && 'Your account has been deactivated'}
                    {member?.status === 'suspended' && 'Your account has been suspended by leadership'}
                  </span>
                </div>
              </div>
              
              {probationStatus && probationStatus.on_probation && (
                <div data-testid="probation-detail-section" style={{
                  background: 'linear-gradient(135deg, #FFFBEB, #FEF3C7)',
                  border: '1px solid #FCD34D',
                  borderRadius: '12px',
                  padding: '20px',
                  marginTop: '16px'
                }}>
                  <h3 style={{ margin: '0 0 16px', color: '#92400E', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    Probation Period Active
                  </h3>

                  {/* Reason */}
                  <div style={{ background: 'white', borderRadius: '8px', padding: '12px 16px', marginBottom: '14px' }}>
                    <div style={{ fontSize: '12px', color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Reason</div>
                    <div style={{ color: '#374151', fontSize: '14px' }}>{probationStatus.reason || 'No reason specified'}</div>
                  </div>

                  {/* Progress Bar */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: '#92400E' }}>Progress</span>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#92400E' }}>{probationStatus.progress_percent || 0}% elapsed</span>
                    </div>
                    <div style={{ height: '8px', background: 'rgba(245,158,11,0.15)', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${probationStatus.progress_percent || 0}%`,
                        background: 'linear-gradient(90deg, #F59E0B, #D97706)',
                        borderRadius: '4px',
                        transition: 'width 0.6s ease'
                      }} />
                    </div>
                  </div>

                  {/* Countdown */}
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '14px', justifyContent: 'center' }}>
                    {[
                      { value: probationStatus.expired ? 0 : probationStatus.days_remaining, label: 'Days' },
                      { value: probationStatus.expired ? 0 : probationStatus.hours_remaining, label: 'Hours' },
                      { value: probationStatus.expired ? 0 : (probationStatus.minutes_remaining || 0), label: 'Minutes' }
                    ].map((item) => (
                      <div key={item.label} style={{
                        background: 'white', borderRadius: '10px', padding: '12px 18px', textAlign: 'center',
                        minWidth: '70px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <div data-testid={`countdown-${item.label.toLowerCase()}`} style={{ fontSize: '28px', fontWeight: '800', color: '#92400E' }}>{item.value}</div>
                        <div style={{ fontSize: '11px', color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Dates */}
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '130px', background: 'white', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontSize: '11px', color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Started</div>
                      <div style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                        {probationStatus.probation_start ? new Date(probationStatus.probation_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: '130px', background: 'white', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontSize: '11px', color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ends</div>
                      <div style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                        {probationStatus.probation_end ? new Date(probationStatus.probation_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: '130px', background: 'white', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontSize: '11px', color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Duration</div>
                      <div style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                        {probationStatus.probation_days || 0} days
                      </div>
                    </div>
                    {probationStatus.set_by_name && (
                      <div style={{ flex: 1, minWidth: '130px', background: 'white', borderRadius: '8px', padding: '10px 14px' }}>
                        <div style={{ fontSize: '11px', color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Set By</div>
                        <div style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>{probationStatus.set_by_name}</div>
                      </div>
                    )}
                  </div>

                  {probationStatus.expired && (
                    <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '8px', padding: '12px', marginTop: '14px', textAlign: 'center' }}>
                      <span style={{ color: '#065F46', fontWeight: '600' }}>Probation period has ended. Your status will be updated shortly.</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="status-details">
                <div className="status-item">
                  <span className="status-label">Member Since:</span>
                  <span className="status-value">{member?.join_date ? new Date(member.join_date).toLocaleDateString() : 'N/A'}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Last Payment:</span>
                  <span className="status-value">{member?.last_payment_date ? new Date(member.last_payment_date).toLocaleDateString() : 'No payments yet'}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Payment Amount:</span>
                  <span className="status-value">${group?.payment_amount}</span>
                </div>
                <div className="status-item">
                  <span className="status-label">Frequency:</span>
                  <span className="status-value">{group?.payment_frequency}</span>
                </div>
              </div>
              
              <div className="action-buttons">
                <div style={{ position: 'relative' }}>
                  <button 
                    onClick={() => {
                      const unpaidCases = paymentNotifications.filter(n => !n.is_paid);
                      if (unpaidCases.length === 0) {
                        toast.error('No active case');
                        return;
                      }
                      if (unpaidCases.length === 1) {
                        handlePayment(unpaidCases[0].case_number);
                        return;
                      }
                      setShowPaymentDropdown(prev => !prev);
                    }}
                    className="btn btn-primary"
                    data-testid="make-payment-btn"
                  >
                    💳 Make Payment
                  </button>
                  {showPaymentDropdown && (
                    <div data-testid="payment-case-dropdown" style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '6px',
                      background: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '10px',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      zIndex: 50,
                      minWidth: '280px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ padding: '10px 14px', borderBottom: '1px solid #F3F4F6', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                        Select a case to pay
                      </div>
                      {paymentNotifications.filter(n => !n.is_paid).map((notif) => (
                        <button
                          key={notif.id}
                          data-testid={`pay-case-${notif.case_number}`}
                          onClick={() => {
                            setShowPaymentDropdown(false);
                            handlePayment(notif.case_number);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '10px 14px',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#1F2937',
                            borderBottom: '1px solid #F9FAFB',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#F3F4F6'}
                          onMouseLeave={(e) => e.target.style.background = 'none'}
                        >
                          <div style={{ fontWeight: '500' }}>📋 {notif.case_number}</div>
                          <div style={{ color: '#6B7280', fontSize: '12px', marginTop: '2px' }}>{notif.title || notif.message?.slice(0, 50)}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => setShowUploadModal(true)} 
                  className="btn btn-secondary"
                  data-testid="upload-proof-btn"
                >
                  Upload Proof
                </button>
                {group?.currency === 'KES' && (
                  <button
                    onClick={() => {
                      const unpaidCases = paymentNotifications.filter(n => !n.is_paid);
                      if (unpaidCases.length === 0) {
                        toast.error('No active case');
                        return;
                      }
                      setMpesaCaseNumber(unpaidCases.length === 1 ? unpaidCases[0].case_number : '');
                      setMpesaStatus(null);
                      setShowMpesaModal(true);
                    }}
                    className="btn"
                    data-testid="mpesa-pay-btn"
                    style={{ background: '#4CAF50', color: 'white', border: 'none', fontWeight: 600 }}
                  >
                    M-Pesa Pay
                  </button>
                )}
              </div>
              
              {/* Alternative Payment Methods */}
              <AlternativePaymentInfo groupId={groupId} caseNumbers={paymentNotifications.filter(n => !n.is_paid).map(n => n.case_number)} hasBankConnection={wallet.has_bank_connection || false} />
              
              <div className="mt-3">
                <NotificationSettings />
              </div>
              
              {myProofs.length > 0 && (
                <div className="my-proofs-section">
                  <h4>📋 Your Submitted Proofs</h4>
                  <div className="proofs-list">
                    {myProofs.slice(0, 3).map((proof) => (
                      <div key={proof.id} className="proof-item-small">
                        <span className={`proof-status ${proof.verified ? 'verified' : 'pending'}`}>
                          {proof.verified ? '✓ Verified' : '⏳ Pending Review'}
                        </span>
                        <span className="proof-date">{new Date(proof.uploaded_at).toLocaleDateString()}</span>
                        <a href={proof.file_url} target="_blank" rel="noopener noreferrer" className="view-link">
                          View
                        </a>
                        {!proof.verified && (
                          <button
                            onClick={() => handleDeleteProof(proof.id)}
                            className="delete-proof-btn"
                            title="Delete this proof"
                            style={{
                              background: '#FEE2E2',
                              color: '#DC2626',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              marginLeft: '8px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Wallet Tab */}
        {activeTab === 'wallet' && (
          <div className="tab-content">
            {/* Wallet Sub-tabs */}
            <div className="wallet-subtabs" data-testid="wallet-subtabs">
              <button
                className={`subtab-btn ${walletSubTab === 'balance' ? 'active' : ''}`}
                onClick={() => setWalletSubTab('balance')}
                data-testid="wallet-subtab-balance"
              >
                Balance
              </button>
              <button
                className={`subtab-btn ${walletSubTab === 'prepaid' ? 'active' : ''}`}
                onClick={() => setWalletSubTab('prepaid')}
                data-testid="wallet-subtab-prepaid"
              >
                Prepaid
                {wallet.is_low_balance && wallet.prepaid_active && (
                  <span className="subtab-badge warning">!</span>
                )}
              </button>
            </div>

            {/* Balance Sub-tab */}
            {walletSubTab === 'balance' && (
            <div className="wallet-section card">
              <div className="wallet-header">
                <h2>Wallet Balance</h2>
              </div>
              
              <div className="wallet-balance-display">
                <div className="balance-amount">
                  <span className="currency">{getCurrencySymbol(group?.currency)}</span>
                  <span className="amount">{(wallet.wallet_balance || 0).toFixed(2)}</span>
                </div>
                <span className="balance-label">Available Balance</span>
              </div>
              
              <div className="wallet-actions">
                <button 
                  onClick={() => setShowTopUpModal(true)}
                  className="btn btn-primary"
                  data-testid="topup-wallet-btn"
                >
                  Top Up Wallet
                </button>
              </div>

              <div className="autopay-toggle-container" data-testid="wallet-autopay-section">
                <div className="autopay-info">
                  <span className="autopay-label">Autopay</span>
                  <p className="autopay-description">
                    Automatically pay contributions from your wallet when cases are approved
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={wallet.autopay_enabled || false}
                    onChange={handleToggleAutopay}
                    data-testid="autopay-toggle"
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {wallet.autopay_enabled && (
                <div className="autopay-enabled-notice">
                  <span className="check-icon">&#10003;</span>
                  <span>Autopay is enabled. Payments will be automatically deducted from your wallet when leadership approves a case.</span>
                </div>
              )}

              {wallet.is_low_balance && wallet.wallet_balance > 0 && (
                <div className="low-balance-alert">
                  <div className="alert-content">
                    <strong>Low Balance Alert</strong>
                    <p>Your balance is below {formatCurrency(wallet.low_balance_threshold || 0, group?.currency)}. Top up to ensure autopay works for the next contribution.</p>
                  </div>
                </div>
              )}
              
              {walletTransactions.length > 0 && (
                <div className="wallet-transactions">
                  <h4>Transaction History</h4>
                  <div className="transactions-list">
                    {walletTransactions.map((tx) => (
                      <div key={tx.id} className={`transaction-item ${tx.type}`}>
                        <div className="tx-info">
                          <span className="tx-type">{tx.type === 'credit' ? '+' : '-'} {tx.type === 'credit' ? 'Credit' : 'Debit'}</span>
                          <span className="tx-notes">{tx.notes || tx.method}</span>
                        </div>
                        <div className="tx-amount-info">
                          <span className={`tx-amount ${tx.type}`}>
                            {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount, group?.currency)}
                          </span>
                          <span className="tx-date">{new Date(tx.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Prepaid Sub-tab */}
            {walletSubTab === 'prepaid' && (
            <div className="wallet-section card" data-testid="prepaid-section">
              {!wallet.prepaid_active ? (
                <div className="prepaid-inactive-notice" data-testid="prepaid-inactive">
                  <div className="inactive-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                  </div>
                  {wallet.prepaid_disabled_by_leadership ? (
                    <>
                      <h3>Prepaid Services Disabled</h3>
                      <p>Your group leadership has disabled prepaid auto-payment services for this group.</p>
                      <p className="inactive-hint">Contact your group leadership for more information.</p>
                    </>
                  ) : (
                    <>
                      <h3>Prepaid Feature Not Available</h3>
                      <p>The prepaid auto-payment feature requires your group to have a linked bank account (Plaid) or a payment method on file (Stripe).</p>
                      <p className="inactive-hint">Ask your group leadership to connect a bank account or add a payment method to enable this feature.</p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="wallet-header">
                    <h2>Prepaid Auto-Payment</h2>
                    {wallet.is_low_balance && (
                      <span className="low-balance-badge" data-testid="prepaid-low-balance-badge">Low Balance</span>
                    )}
                  </div>

                  <div className="prepaid-balance-card" data-testid="prepaid-balance-card">
                    <div className="prepaid-balance-top">
                      <div className="wallet-balance-display">
                        <div className="balance-amount">
                          <span className="currency">{getCurrencySymbol(group?.currency)}</span>
                          <span className="amount">{(wallet.wallet_balance || 0).toFixed(2)}</span>
                        </div>
                        <span className="balance-label">Prepaid Balance</span>
                      </div>
                      <div className="cases-covered-display" data-testid="cases-covered">
                        <span className="cases-number">{wallet.cases_covered || 0}</span>
                        <span className="cases-label">case{wallet.cases_covered !== 1 ? 's' : ''} covered</span>
                      </div>
                    </div>

                    {/* Progress bar: cases covered out of 3 minimum */}
                    <div className="prepaid-progress">
                      <div className="progress-bar-container">
                        <div 
                          className={`progress-bar-fill ${wallet.cases_covered >= 3 ? 'healthy' : wallet.cases_covered >= 1 ? 'warning' : 'danger'}`}
                          style={{ width: `${Math.min((wallet.cases_covered / 3) * 100, 100)}%` }}
                          data-testid="prepaid-progress-bar"
                        ></div>
                      </div>
                      <span className="progress-label">
                        {wallet.cases_covered >= 3 
                          ? 'Balance is healthy' 
                          : `${3 - wallet.cases_covered} more case${3 - wallet.cases_covered !== 1 ? 's' : ''} needed for minimum coverage`}
                      </span>
                    </div>
                  </div>

                  {wallet.is_low_balance && (
                    <div className="low-balance-alert" data-testid="prepaid-low-balance-alert">
                      <div className="alert-content">
                        <strong>Low Balance - Top Up Required</strong>
                        <p>Your prepaid balance covers fewer than 3 cases. If your balance is insufficient when a case is approved, you will be marked as <strong>unpaid</strong>.</p>
                        <p>Recommended top-up: <strong>{formatCurrency((wallet.contribution_amount || 0) * 3, group?.currency)}</strong> (covers 3 cases)</p>
                      </div>
                    </div>
                  )}

                  <div className="autopay-toggle-container">
                    <div className="autopay-info">
                      <span className="autopay-label">Auto-Pay from Prepaid</span>
                      <p className="autopay-description">
                        Automatically pay from your prepaid balance when case notices are approved
                      </p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={wallet.autopay_enabled || false}
                        onChange={handleToggleAutopay}
                        data-testid="prepaid-autopay-toggle"
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {wallet.autopay_enabled && (
                    <div className="autopay-enabled-notice">
                      <span className="check-icon">&#10003;</span>
                      <span>Auto-pay is enabled. Approved case notices will be automatically paid from your prepaid balance.</span>
                    </div>
                  )}

                  {!wallet.autopay_enabled && (
                    <div className="autopay-disabled-warning">
                      <strong>Auto-pay is off.</strong> Enable it to automatically pay for approved cases from your prepaid balance.
                    </div>
                  )}

                  <div className="wallet-actions">
                    <button 
                      onClick={() => setShowTopUpModal(true)}
                      className="btn btn-primary"
                      data-testid="prepaid-topup-btn"
                    >
                      Top Up Prepaid
                    </button>
                  </div>

                  {/* Auto Top-Up Section */}
                  <div className="auto-topup-section" data-testid="auto-topup-section">
                    <div className="auto-topup-header">
                      <div className="auto-topup-title-row">
                        <h4>Auto Top-Up</h4>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={wallet.auto_topup_enabled || false}
                            onChange={handleToggleAutoTopUp}
                            disabled={savingAutoTopUp}
                            data-testid="auto-topup-toggle"
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <p className="auto-topup-desc">Automatically charge your saved card when your prepaid balance runs low</p>
                    </div>

                    {wallet.auto_topup_card_last4 ? (
                      <div className="saved-card-display" data-testid="saved-card-info">
                        <div className="card-info">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6D28D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                            <line x1="1" y1="10" x2="23" y2="10"/>
                          </svg>
                          <span className="card-detail">
                            {(wallet.auto_topup_card_brand || 'Card').charAt(0).toUpperCase() + (wallet.auto_topup_card_brand || 'card').slice(1)} ending in {wallet.auto_topup_card_last4}
                          </span>
                        </div>
                        <button className="btn-link-danger" onClick={handleRemoveCard} data-testid="remove-card-btn">
                          Remove
                        </button>
                      </div>
                    ) : (
                      !showCardSetup && (
                        <button
                          className="btn btn-secondary add-card-btn"
                          onClick={() => setShowCardSetup(true)}
                          data-testid="add-card-btn"
                        >
                          Add Card for Auto Top-Up
                        </button>
                      )
                    )}

                    {showCardSetup && (
                      <Elements stripe={stripePromise}>
                        <CardSetupForm
                          memberId={member?.id}
                          onSuccess={handleCardSaved}
                          onCancel={() => setShowCardSetup(false)}
                        />
                      </Elements>
                    )}

                    {wallet.auto_topup_card_last4 && (
                      <div className="auto-topup-settings" data-testid="auto-topup-settings">
                        <div className="settings-row">
                          <label>Top-up amount ({getCurrencySymbol(group?.currency)})</label>
                          <input
                            type="number"
                            min="5"
                            step="0.01"
                            value={autoTopUpAmount}
                            onChange={(e) => setAutoTopUpAmount(e.target.value)}
                            placeholder={String((wallet.contribution_amount || 50) * 3)}
                            data-testid="auto-topup-amount-input"
                          />
                        </div>
                        <div className="settings-row">
                          <label>Trigger when balance covers fewer than</label>
                          <div className="threshold-select">
                            <select
                              value={autoTopUpThreshold}
                              onChange={(e) => setAutoTopUpThreshold(parseInt(e.target.value))}
                              data-testid="auto-topup-threshold-select"
                            >
                              {[1,2,3,4,5].map(n => (
                                <option key={n} value={n}>{n} case{n !== 1 ? 's' : ''}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          className="btn btn-primary btn-small"
                          onClick={handleSaveAutoTopUpSettings}
                          disabled={savingAutoTopUp}
                          data-testid="save-auto-topup-settings-btn"
                        >
                          {savingAutoTopUp ? 'Saving...' : 'Save Settings'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="prepaid-info-card">
                    <h4>How Prepaid Works</h4>
                    <ul className="prepaid-how-list">
                      <li>Top up your prepaid balance with a lump sum</li>
                      <li>When a case notice is approved, payment is automatically deducted</li>
                      <li>You receive a notification for each processed payment</li>
                      <li>If balance is insufficient, you are marked as unpaid and reminded to top up</li>
                      <li>Low balance alerts are sent when you have fewer than 3 cases covered</li>
                    </ul>
                  </div>

                  {prepaidTransactions.length > 0 && (
                    <div className="wallet-transactions">
                      <h4>Prepaid Payment History</h4>
                      <div className="transactions-list">
                        {prepaidTransactions.map((tx) => (
                          <div key={tx.id} className={`transaction-item ${tx.type}`}>
                            <div className="tx-info">
                              <span className="tx-type">{tx.type === 'credit' ? '+' : '-'} {tx.type === 'credit' ? 'Top-Up' : 'Auto-Payment'}</span>
                              <span className="tx-notes">{tx.notes || tx.method}{tx.case_number ? ` | Case #${tx.case_number}` : ''}</span>
                            </div>
                            <div className="tx-amount-info">
                              <span className={`tx-amount ${tx.type}`}>
                                {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount, group?.currency)}
                              </span>
                              <span className="tx-date">{new Date(tx.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {prepaidTransactions.length === 0 && (
                    <div className="empty-state-small">
                      <p>No prepaid transactions yet. Top up your balance and enable auto-pay to get started.</p>
                    </div>
                  )}
                </>
              )}
            </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="tab-content">
            <div className="payments-section card">
              <div className="section-header">
                <h2>Payment History</h2>
                {payments.length > 0 && (
                  <button 
                    onClick={generatePaymentHistoryPDF}
                    className="btn btn-secondary btn-small"
                    title="Download PDF"
                  >
                    📥 Download PDF
                  </button>
                )}
              </div>
              
              {payments.length > 0 && (
                <div className="filter-controls">
                  <div className="filter-row">
                    <div className="filter-item">
                      <label>Filter by Case Number:</label>
                      <select 
                        value={filterCaseNumber} 
                        onChange={(e) => setFilterCaseNumber(e.target.value)}
                        className="filter-select"
                      >
                        <option value="">All Cases</option>
                        {caseNumbers.map((caseNum) => (
                          <option key={caseNum} value={caseNum}>{caseNum}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="filter-item">
                      <label>Or Search Case:</label>
                      <input 
                        type="text" 
                        value={filterCaseNumber} 
                        onChange={(e) => setFilterCaseNumber(e.target.value)}
                        placeholder="Type case number..."
                        className="filter-input"
                      />
                    </div>
                  </div>
                  
                  <div className="filter-row">
                    <div className="filter-item">
                      <label>Start Date:</label>
                      <input 
                        type="date" 
                        value={filterStartDate} 
                        onChange={(e) => setFilterStartDate(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    
                    <div className="filter-item">
                      <label>End Date:</label>
                      <input 
                        type="date" 
                        value={filterEndDate} 
                        onChange={(e) => setFilterEndDate(e.target.value)}
                        className="filter-input"
                      />
                    </div>
                    
                    <button 
                      onClick={clearFilters}
                      className="btn btn-secondary btn-small"
                    >
                      Clear Filters
                    </button>
                  </div>
                  
                  <div className="filter-summary">
                    Showing {filteredPayments.length} of {payments.length} payments
                  </div>
                </div>
              )}
              
              {filteredPayments.length > 0 && (
                <div data-testid="member-payments-total-bar" style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
                  background: 'linear-gradient(135deg, #1E3A5F, #1E3A5F)',
                  borderRadius: '10px', padding: '14px 20px', marginBottom: '16px', color: 'white'
                }}>
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Total Amount</div>
                      <div data-testid="member-payments-total-amount" style={{ fontSize: '22px', fontWeight: 700 }}>
                        {formatCurrency(filteredPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0), group?.currency)}
                      </div>
                    </div>
                    <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '20px' }}>
                      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Records</div>
                      <div data-testid="member-payments-total-count" style={{ fontSize: '18px', fontWeight: 600 }}>{filteredPayments.length}</div>
                    </div>
                    {filterCaseNumber && (
                      <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '20px' }}>
                        <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Case</div>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>{filterCaseNumber}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.7 }}>
                    {filterCaseNumber || filterStartDate || filterEndDate ? 'Filtered results' : 'All payments'}
                  </div>
                </div>
              )}
              
              {payments.length === 0 ? (
                <div className="empty-section">
                  <div className="empty-icon">💳</div>
                  <p>No payments yet</p>
                  <p style={{ fontSize: '13px', color: '#6B7280' }}>Payment history will appear here once you make a payment from the Overview tab.</p>
                </div>
              ) : filteredPayments.length === 0 ? (
                <div className="empty-section">
                  <p>No payments match the selected filters</p>
                </div>
              ) : (
                <div className="payments-list">
                  {filteredPayments.map((payment) => (
                    <div key={payment.id} className="payment-item">
                      <div className="payment-info">
                        <div className="payment-date">
                          📅 {new Date(payment.date).toLocaleDateString()}
                        </div>
                        {payment.case_number && (
                          <div className="payment-case">
                            📋 Case: {payment.case_number}
                          </div>
                        )}
                      </div>
                      <div className="payment-amount">{formatCurrency(payment.amount, group?.currency)}</div>
                      <span className="status-badge status-approved">{payment.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="tab-content">
            {paymentNotifications.length === 0 && broadcasts.length === 0 ? (
              <div className="card empty-section">
                <div className="empty-icon">🔔</div>
                <p>No notifications yet</p>
                <span className="empty-subtext">You'll see payment reminders and announcements here</span>
              </div>
            ) : (
              <>
                {paymentNotifications.length > 0 && (
                  <div className="notifications-section">
                    <h2>💰 Payment Reminders ({paymentNotifications.length})</h2>
                    <div className="notifications-list">
                      {paymentNotifications.map((notification, index) => (
                        <div 
                          key={notification.id} 
                          className={`notification-item ${!notification.read ? 'unread' : ''} ${notification.is_paid ? 'paid' : ''}`}
                          style={{
                            border: notification.is_paid ? '3px solid #10B981' : '3px solid #CBD5E1',
                            borderRadius: '16px',
                            padding: '28px',
                            background: notification.is_paid ? '#ECFDF5' : 'white',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                            marginBottom: '28px',
                            position: 'relative'
                          }}
                        >
                          {/* Paid Badge */}
                          {notification.is_paid && (
                            <div className="paid-badge" style={{
                              position: 'absolute',
                              top: '16px',
                              left: '16px',
                              background: 'linear-gradient(135deg, #10B981, #059669)',
                              color: 'white',
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                            }}>
                              ✓ Paid
                            </div>
                          )}
                          <div 
                            className="notification-top-bar"
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '6px',
                              background: notification.is_paid 
                                ? 'linear-gradient(90deg, #10B981, #059669)' 
                                : 'linear-gradient(90deg, #3B82F6, #1D4ED8)',
                              borderRadius: '14px 14px 0 0'
                            }}
                          />
                          <span className="notification-number" style={{
                            position: 'absolute',
                            top: '-14px',
                            right: '24px',
                            background: notification.is_paid 
                              ? 'linear-gradient(135deg, #10B981, #059669)'
                              : 'linear-gradient(135deg, #1D4ED8, #2563EB)',
                            color: 'white',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: '700',
                            boxShadow: notification.is_paid 
                              ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                              : '0 4px 12px rgba(29, 78, 216, 0.4)',
                            border: '3px solid white'
                          }}>{notification.is_paid ? '✓' : index + 1}</span>
                          <div className="notification-header" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px solid #E5E7EB',
                            marginTop: notification.is_paid ? '24px' : '0'
                          }}>
                            <h3 style={{margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1F2937'}}>{notification.title}</h3>
                            <span className="notification-date" style={{
                              fontSize: '0.875rem',
                              color: '#6B7280',
                              background: '#F3F4F6',
                              padding: '4px 10px',
                              borderRadius: '6px'
                            }}>
                              {new Date(notification.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {notification.case_number && (
                            <p className="case-number-badge" style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              background: notification.is_paid ? '#D1FAE5' : '#EFF6FF',
                              color: notification.is_paid ? '#065F46' : '#1E40AF',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '500',
                              marginBottom: '12px'
                            }}>
                              📋 Case: {notification.case_number}
                              {notification.is_paid && <span style={{color: '#10B981'}}>✓</span>}
                            </p>
                          )}
                          <p className="notification-message">{notification.message}</p>
                          
                          {notification.deadline && !notification.is_paid && (
                            <div data-testid="grace-period-countdown" style={{
                              background: new Date(notification.deadline) > new Date() 
                                ? 'linear-gradient(135deg, #FEF3C7, #FDE68A)' 
                                : 'linear-gradient(135deg, #FEE2E2, #FECACA)',
                              border: `1px solid ${new Date(notification.deadline) > new Date() ? '#F59E0B' : '#EF4444'}`,
                              borderRadius: '10px',
                              padding: '16px',
                              marginTop: '12px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                                <div>
                                  <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: new Date(notification.deadline) > new Date() ? '#92400E' : '#991B1B', fontWeight: '600' }}>
                                    {new Date(notification.deadline) > new Date() ? 'Grace Period' : 'Grace Period Expired'}
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#374151', marginTop: '2px' }}>
                                    Deadline: {new Date(notification.deadline).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </div>
                                </div>
                                {new Date(notification.deadline) > new Date() && (() => {
                                  const diff = new Date(notification.deadline) - new Date();
                                  const days = Math.floor(diff / 86400000);
                                  const hours = Math.floor((diff % 86400000) / 3600000);
                                  const minutes = Math.floor((diff % 3600000) / 60000);
                                  return (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      {[{ v: days, l: 'Days' }, { v: hours, l: 'Hrs' }, { v: minutes, l: 'Min' }].map(t => (
                                        <div key={t.l} style={{
                                          background: 'white', borderRadius: '8px', padding: '6px 10px', textAlign: 'center',
                                          minWidth: '48px', boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                                        }}>
                                          <div style={{ fontSize: '18px', fontWeight: '800', color: '#92400E' }}>{t.v}</div>
                                          <div style={{ fontSize: '9px', color: '#A16207', textTransform: 'uppercase' }}>{t.l}</div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                                {new Date(notification.deadline) <= new Date() && (
                                  <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                                    EXPIRED
                                  </span>
                                )}
                              </div>

                              {/* Progress bar */}
                              {(() => {
                                const created = new Date(notification.created_at);
                                const deadline = new Date(notification.deadline);
                                const now = new Date();
                                const total = deadline - created;
                                const elapsed = now - created;
                                const pct = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
                                const barColor = pct >= 100 ? '#EF4444' : pct >= 70 ? '#F59E0B' : '#22C55E';
                                return (
                                  <div style={{ marginBottom: '12px' }}>
                                    <div style={{ height: '6px', background: 'rgba(0,0,0,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: '3px', transition: 'width 0.5s ease' }} />
                                    </div>
                                  </div>
                                );
                              })()}

                              <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: '8px', padding: '10px 12px', fontSize: '13px', color: '#374151' }}>
                                <strong>If deadline is missed:</strong>
                                <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                                  <li>Account marked <strong>Inactive</strong> immediately</li>
                                  {notification.inactive_window_days && (
                                    <li>{notification.inactive_window_days} days to pay while Inactive</li>
                                  )}
                                  {notification.penalty_amount > 0 && (
                                    <li>Late penalty: {formatCurrency(notification.penalty_amount, group?.currency)}</li>
                                  )}
                                  {notification.probation_days && (
                                    <li>After payment: {notification.probation_days}-day probation before Active</li>
                                  )}
                                  {notification.inactive_window_days && (
                                    <li>No payment within {notification.inactive_window_days} days → <strong>Suspended</strong></li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                          
                          {notification.is_paid ? (
                            <div className="paid-confirmation" style={{
                              background: '#D1FAE5',
                              border: '1px solid #10B981',
                              borderRadius: '8px',
                              padding: '12px 16px',
                              marginTop: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px'
                            }}>
                              <span style={{fontSize: '20px'}}>✅</span>
                              <div>
                                <strong style={{color: '#065F46'}}>Payment Completed</strong>
                                <p style={{margin: 0, fontSize: '13px', color: '#047857'}}>
                                  You have already paid for this case. Thank you!
                                </p>
                              </div>
                            </div>
                          ) : (
                            <button 
                              onClick={() => handlePayment(notification.case_number)}
                              className="btn btn-primary btn-small"
                            >
                              💳 Make Payment Now
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {broadcasts.length > 0 && (
                  <div className="broadcasts-section">
                    <h2>📢 Announcements</h2>
                    <div className="broadcasts-list">
                      {broadcasts.map((broadcast) => (
                        <div key={broadcast.id} className="broadcast-item">
                          <div className="broadcast-header">
                            <div>
                              <span className="broadcast-type-badge">
                                {broadcast.type === 'meeting' ? '📅 Meeting' : '📢 Broadcast'}
                              </span>
                              <h3>{broadcast.title}</h3>
                            </div>
                            <span className="broadcast-date">
                              {new Date(broadcast.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="broadcast-message">{broadcast.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Meetings Tab */}
        {activeTab === 'meetings' && (
          <div className="tab-content">
            <div className="meetings-section card">
              <h2>📅 Upcoming Meetings</h2>
              {meetings.length === 0 ? (
                <div className="empty-section">
                  <div className="empty-icon">📅</div>
                  <p>No meetings scheduled</p>
                  <span className="empty-subtext">Check back later for upcoming group meetings</span>
                </div>
              ) : (
                <div className="meetings-list">
                  {meetings.map((meeting) => (
                    <div key={meeting.id} className="meeting-item">
                      <div className="meeting-date-display">
                        <span className="meeting-month">{new Date(meeting.date).toLocaleDateString('en-US', { month: 'short' })}</span>
                        <span className="meeting-day">{new Date(meeting.date).getDate()}</span>
                      </div>
                      <div className="meeting-details">
                        <h3>{meeting.title}</h3>
                        <p className="meeting-time">
                          🕐 {new Date(meeting.date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </p>
                        {meeting.location && <p className="meeting-location">📍 {meeting.location}</p>}
                        <p className="meeting-description">{meeting.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="tab-content">
            <div className="support-section card">
              <div className="support-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>🎫 Support Tickets</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowTicketModal(true)}
                  data-testid="new-ticket-btn"
                >
                  + New Ticket
                </button>
              </div>
              
              <div className="support-info" style={{
                background: '#EFF6FF',
                border: '1px solid #BFDBFE',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>💡</span>
                <div>
                  <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#1E40AF' }}>Need Help?</p>
                  <p style={{ margin: 0, color: '#3B82F6', fontSize: '14px' }}>
                    Submit a ticket for questions about payments, account issues, or any group-related queries.
                    Your group leadership will respond as soon as possible.
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: '#6B7280', fontSize: '13px' }}>
                    📧 You can also reach us at: <a href="mailto:support@chamalink.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB' }} onClick={(e) => e.stopPropagation()}>support@chamalink.com</a>
                  </p>
                </div>
              </div>
              
              {supportTickets.length === 0 ? (
                <div className="empty-section">
                  <div className="empty-icon">🎫</div>
                  <p>No support tickets</p>
                  <span className="empty-subtext">Click "New Ticket" to create your first support request</span>
                </div>
              ) : (
                <div className="tickets-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {supportTickets.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      className="ticket-item"
                      onClick={() => setSelectedTicket(ticket)}
                      style={{
                        background: 'white',
                        border: ticket.status === 'resolved' ? '2px solid #10B981' : '2px solid #E5E7EB',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <span style={{ 
                            background: '#F3F4F6', 
                            color: '#6B7280', 
                            padding: '2px 8px', 
                            borderRadius: '4px', 
                            fontSize: '12px',
                            marginRight: '8px'
                          }}>
                            {ticket.ticket_number}
                          </span>
                          <span style={{
                            background: ticket.status === 'open' ? '#FEF3C7' : 
                                        ticket.status === 'in_progress' ? '#DBEAFE' : 
                                        ticket.status === 'resolved' ? '#D1FAE5' : '#F3F4F6',
                            color: ticket.status === 'open' ? '#92400E' : 
                                   ticket.status === 'in_progress' ? '#1E40AF' : 
                                   ticket.status === 'resolved' ? '#065F46' : '#374151',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            textTransform: 'capitalize'
                          }}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </div>
                        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 style={{ margin: '0 0 4px 0', color: '#1F2937' }}>{ticket.subject}</h4>
                      <p style={{ margin: 0, color: '#6B7280', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ticket.message}
                      </p>
                      {ticket.replies?.length > 0 && (
                        <p style={{ margin: '8px 0 0 0', color: '#3B82F6', fontSize: '13px' }}>
                          💬 {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Claims Tab */}
      {activeTab === 'claims' && (
        <ClaimsTab
          member={member}
          groupId={groupId}
          beneficiaries={beneficiaries}
          setBeneficiaries={setBeneficiaries}
          claims={claims}
          setClaims={setClaims}
          showBeneficiaryModal={showBeneficiaryModal}
          setShowBeneficiaryModal={setShowBeneficiaryModal}
          editingBeneficiary={editingBeneficiary}
          setEditingBeneficiary={setEditingBeneficiary}
          beneficiaryForm={beneficiaryForm}
          setBeneficiaryForm={setBeneficiaryForm}
          beneficiaryDobDay={beneficiaryDobDay}
          setBeneficiaryDobDay={setBeneficiaryDobDay}
          beneficiaryDobMonth={beneficiaryDobMonth}
          setBeneficiaryDobMonth={setBeneficiaryDobMonth}
          beneficiaryDobYear={beneficiaryDobYear}
          setBeneficiaryDobYear={setBeneficiaryDobYear}
          savingBeneficiary={savingBeneficiary}
          setSavingBeneficiary={setSavingBeneficiary}
          currencyCode={group?.currency}
          changeRequests={beneficiaryChangeRequests}
          setChangeRequests={setBeneficiaryChangeRequests}
        />
      )}

      {/* New Ticket Modal */}
      {showTicketModal && (
        <div className="modal-overlay" onClick={() => setShowTicketModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>🎫 Create Support Ticket</h2>
              <button className="modal-close" onClick={() => setShowTicketModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Category</label>
                <select
                  value={ticketCategory}
                  onChange={(e) => setTicketCategory(e.target.value)}
                  data-testid="ticket-category-select"
                >
                  <option value="general">General Question</option>
                  <option value="payment">Payment Issue</option>
                  <option value="account">Account Problem</option>
                  <option value="technical">Technical Issue</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  data-testid="ticket-subject-input"
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows="5"
                  data-testid="ticket-message-input"
                />
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowTicketModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSubmitTicket}
                  disabled={submittingTicket || !ticketSubject.trim() || !ticketMessage.trim()}
                  data-testid="submit-ticket-btn"
                >
                  {submittingTicket ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>🎫 {selectedTicket.ticket_number}</h2>
              <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <span style={{
                  background: selectedTicket.status === 'open' ? '#FEF3C7' : 
                              selectedTicket.status === 'in_progress' ? '#DBEAFE' : 
                              selectedTicket.status === 'resolved' ? '#D1FAE5' : '#F3F4F6',
                  color: selectedTicket.status === 'open' ? '#92400E' : 
                         selectedTicket.status === 'in_progress' ? '#1E40AF' : 
                         selectedTicket.status === 'resolved' ? '#065F46' : '#374151',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  textTransform: 'capitalize'
                }}>
                  {selectedTicket.status.replace('_', ' ')}
                </span>
                <span style={{ marginLeft: '12px', color: '#6B7280', fontSize: '13px' }}>
                  Created: {new Date(selectedTicket.created_at).toLocaleString()}
                </span>
              </div>
              
              <h3 style={{ margin: '0 0 8px 0' }}>{selectedTicket.subject}</h3>
              <p style={{ 
                background: '#F9FAFB', 
                padding: '12px', 
                borderRadius: '8px', 
                margin: '0 0 20px 0',
                color: '#374151'
              }}>
                {selectedTicket.message}
              </p>
              
              {/* Replies */}
              {selectedTicket.replies?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>💬 Responses</h4>
                  {selectedTicket.replies.map((reply, idx) => (
                    <div key={idx} style={{
                      background: reply.user_role === 'member' ? '#EFF6FF' : '#F0FDF4',
                      borderLeft: `4px solid ${reply.user_role === 'member' ? '#3B82F6' : '#10B981'}`,
                      padding: '12px',
                      borderRadius: '0 8px 8px 0',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '14px' }}>{reply.user_name}</strong>
                        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Form - only if not resolved/closed */}
              {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                <div>
                  <div className="form-group">
                    <label>Add Reply</label>
                    <textarea
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      placeholder="Type your reply..."
                      rows="3"
                    />
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={handleSubmitReply}
                    disabled={!ticketReply.trim()}
                  >
                    Send Reply
                  </button>
                </div>
              )}
              
              {selectedTicket.status === 'resolved' && (
                <div style={{
                  background: '#D1FAE5',
                  border: '1px solid #10B981',
                  borderRadius: '8px',
                  padding: '12px',
                  textAlign: 'center',
                  color: '#065F46'
                }}>
                  ✅ This ticket has been resolved
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loans Tab */}
      {activeTab === 'loans' && group?.category !== 'benevolence' && (
        <div className="tab-content" data-testid="loans-tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>My Loans</h2>
            <button
              className="btn btn-primary"
              onClick={() => setShowLoanRequestModal(true)}
              data-testid="request-loan-btn"
            >
              + Request Loan
            </button>
          </div>

          {memberLoans.length === 0 ? (
            <div className="empty-section">
              <div className="empty-icon">🏦</div>
              <p>No loans yet</p>
              <p style={{ fontSize: '13px', color: '#6B7280' }}>Submit a loan request to get started.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {memberLoans.map(loan => (
                <div key={loan.id} data-testid={`loan-card-${loan.id}`} style={{
                  background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '18px',
                  cursor: 'pointer', transition: 'box-shadow 0.2s',
                  borderLeft: loan.status === 'terms_set' ? '4px solid #F59E0B' : loan.status === 'active' ? '4px solid #10B981' : loan.status === 'pending' ? '4px solid #3B82F6' : '4px solid #E5E7EB'
                }}
                  onClick={() => setSelectedLoan(selectedLoan?.id === loan.id ? null : loan)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '15px', color: '#1E293B' }}>{loan.loan_number}</span>
                      <span style={{ marginLeft: '10px', fontSize: '13px', color: '#6B7280' }}>{loan.purpose}</span>
                    </div>
                    <span data-testid={`loan-status-${loan.id}`} style={{
                      padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                      background: loan.status === 'requested' ? '#FEF3C7' : loan.status === 'terms_set' ? '#FDE68A' : loan.status === 'pending' ? '#DBEAFE' : loan.status === 'approved' ? '#D1FAE5' : loan.status === 'active' ? '#A7F3D0' : loan.status === 'disbursed' ? '#BAE6FD' : loan.status === 'completed' ? '#D1FAE5' : loan.status === 'declined' ? '#FEE2E2' : loan.status === 'defaulted' ? '#FEE2E2' : '#F3F4F6',
                      color: loan.status === 'requested' ? '#92400E' : loan.status === 'terms_set' ? '#78350F' : loan.status === 'pending' ? '#1E40AF' : loan.status === 'approved' || loan.status === 'active' || loan.status === 'completed' ? '#065F46' : loan.status === 'declined' || loan.status === 'defaulted' ? '#991B1B' : '#374151'
                    }}>
                      {loan.status === 'terms_set' ? 'Review Terms' : loan.status.charAt(0).toUpperCase() + loan.status.slice(1)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: '#4B5563' }}>
                    <span>Amount: <strong>{formatCurrency(loan.amount, group?.currency)}</strong></span>
                    <span>Term: <strong>{loan.term_months} months</strong></span>
                    {loan.interest_rate > 0 && <span>Rate: <strong>{loan.interest_rate}%</strong></span>}
                    {(loan.status === 'active' || loan.status === 'disbursed') && (
                      <span>Repaid: <strong>{formatCurrency(loan.amount_repaid || 0, group?.currency)}</strong> / {formatCurrency(loan.total_repayment, group?.currency)}</span>
                    )}
                  </div>

                  {/* Repayment progress bar for active/disbursed loans */}
                  {(loan.status === 'active' || loan.status === 'disbursed') && loan.total_repayment > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                        <span>Repayment Progress</span>
                        <span>{Math.round(((loan.amount_repaid || 0) / loan.total_repayment) * 100)}%</span>
                      </div>
                      <div style={{ height: '8px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(100, ((loan.amount_repaid || 0) / loan.total_repayment) * 100)}%`, background: '#10B981', borderRadius: '4px', transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  )}

                  {/* Expanded loan details */}
                  {selectedLoan?.id === loan.id && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }} onClick={e => e.stopPropagation()}>
                      
                      {/* Terms Set - Accept/Decline */}
                      {loan.status === 'terms_set' && (
                        <div data-testid={`loan-terms-review-${loan.id}`}>
                          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1E293B', marginBottom: '14px' }}>Loan Terms & Conditions</h3>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', background: '#F9FAFB', padding: '14px', borderRadius: '10px', marginBottom: '14px' }}>
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Loan Amount</span><div style={{ fontWeight: '600' }}>{formatCurrency(loan.amount, group?.currency)}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Term</span><div style={{ fontWeight: '600' }}>{loan.term_months} months</div></div>
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Interest Rate</span><div style={{ fontWeight: '600' }}>{describeLoanRate(loan, getCurrencySymbol(group?.currency))}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Total Interest</span><div style={{ fontWeight: '600' }}>{formatCurrency(loan.total_interest, group?.currency)}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Total Repayment</span><div style={{ fontWeight: '600', color: '#DC2626' }}>{formatCurrency(loan.total_repayment, group?.currency)}</div></div>
                            {loan.origination_fee_amount > 0 && <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Origination Fee</span><div style={{ fontWeight: '600' }}>{formatCurrency(loan.origination_fee_amount, group?.currency)}</div></div>}
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Disbursement</span><div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{loan.disbursement_method?.replace('_', ' ')}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#6B7280' }}>Grace Period</span><div style={{ fontWeight: '600' }}>{loan.grace_period_days} days</div></div>
                          </div>

                          {loan.leadership_notes && (
                            <div style={{ background: '#FEF3C7', padding: '12px', borderRadius: '8px', marginBottom: '14px', fontSize: '13px' }}>
                              <strong>Leadership Notes:</strong> {loan.leadership_notes}
                            </div>
                          )}

                          {/* Repayment Schedule Preview */}
                          {loan.repayment_schedule && loan.repayment_schedule.length > 0 && (
                            <div style={{ marginBottom: '14px' }}>
                              <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Repayment Schedule</h4>
                              <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: '#F9FAFB' }}>
                                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>#</th>
                                      <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>Due Date</th>
                                      <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Principal</th>
                                      <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Interest</th>
                                      <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {loan.repayment_schedule.map((entry, idx) => (
                                      <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                                        <td style={{ padding: '8px 10px' }}>{idx + 1}</td>
                                        <td style={{ padding: '8px 10px' }}>{new Date(entry.due_date).toLocaleDateString()}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatCurrency(entry.principal_amount, group?.currency)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatCurrency(entry.interest_amount, group?.currency)}</td>
                                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(entry.total_amount, group?.currency)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleDeclineLoan(loan.id)}
                              className="btn btn-secondary"
                              data-testid={`decline-loan-${loan.id}`}
                              style={{ background: '#FEE2E2', color: '#991B1B', border: 'none' }}
                            >
                              Decline Offer
                            </button>
                            <button
                              onClick={() => handleAcceptLoan(loan.id)}
                              className="btn btn-primary"
                              data-testid={`accept-loan-${loan.id}`}
                              style={{ background: '#059669', color: 'white', border: 'none' }}
                            >
                              Accept & Sign
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Requested - Waiting for leadership */}
                      {loan.status === 'requested' && (
                        <div style={{ textAlign: 'center', padding: '16px', color: '#6B7280' }}>
                          <span style={{ fontSize: '24px' }}>⏳</span>
                          <p style={{ margin: '8px 0 0' }}>Your loan request is being reviewed by leadership. They will set the terms and conditions.</p>
                        </div>
                      )}

                      {/* Pending approval */}
                      {loan.status === 'pending' && (
                        <div style={{ textAlign: 'center', padding: '16px', color: '#6B7280' }}>
                          <span style={{ fontSize: '24px' }}>✍️</span>
                          <p style={{ margin: '8px 0 0' }}>You accepted the terms. Awaiting leadership signatures ({loan.signed_by?.length || 0}/{loan.signatory_ids?.length || 3} signed).</p>
                        </div>
                      )}

                      {/* Active/Disbursed - Show repayment schedule */}
                      {(loan.status === 'active' || loan.status === 'disbursed' || loan.status === 'approved' || loan.status === 'completed') && loan.repayment_schedule && (
                        <div>
                          <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Repayment Schedule</h4>
                          <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: '#F9FAFB' }}>
                                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>#</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>Due Date</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Amount</th>
                                  <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {loan.repayment_schedule.map((entry, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6', background: entry.status === 'paid' ? '#F0FDF4' : entry.status === 'overdue' ? '#FEF2F2' : 'white' }}>
                                    <td style={{ padding: '8px 10px' }}>{idx + 1}</td>
                                    <td style={{ padding: '8px 10px' }}>{new Date(entry.due_date).toLocaleDateString()}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatCurrency(entry.total_amount, group?.currency)}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                      <span style={{
                                        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                                        background: entry.status === 'paid' ? '#D1FAE5' : entry.status === 'overdue' ? '#FEE2E2' : entry.status === 'partial' ? '#FEF3C7' : '#F3F4F6',
                                        color: entry.status === 'paid' ? '#065F46' : entry.status === 'overdue' ? '#991B1B' : entry.status === 'partial' ? '#92400E' : '#374151'
                                      }}>
                                        {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {loan.next_payment_date && (loan.status === 'active' || loan.status === 'disbursed') && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '12px', background: '#F0FDF4', borderRadius: '10px', border: '1px solid #BBF7D0' }}>
                              <div>
                                <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                                  Next payment due: <strong style={{ color: '#1F2937' }}>{new Date(loan.next_payment_date).toLocaleDateString()}</strong>
                                </p>
                                {(() => {
                                  const nextEntry = loan.repayment_schedule?.find(e => e.status === 'pending' || e.status === 'partial');
                                  const amountDue = nextEntry ? (nextEntry.total_amount - (nextEntry.paid_amount || 0)) : 0;
                                  return amountDue > 0 ? (
                                    <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: '600', color: '#059669' }}>
                                      Amount due: {formatCurrency(amountDue, group?.currency)}
                                    </p>
                                  ) : null;
                                })()}
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleLoanRepayment(loan); }}
                                className="btn btn-primary"
                                data-testid={`pay-loan-${loan.id}`}
                                style={{ background: '#059669', border: 'none', whiteSpace: 'nowrap', padding: '10px 20px', fontSize: '14px', fontWeight: '600' }}
                              >
                                Pay Now
                              </button>
                            </div>
                          )}

                          {/* Repayment history */}
                          {loan.repayments && loan.repayments.length > 0 && (
                            <div style={{ marginTop: '14px' }}>
                              <h4 style={{ fontSize: '14px', marginBottom: '8px' }}>Payment History</h4>
                              {loan.repayments.map((r, idx) => (
                                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderBottom: '1px solid #F3F4F6', fontSize: '13px' }}>
                                  <span>{new Date(r.created_at).toLocaleDateString()} - {r.payment_method}</span>
                                  <span style={{ fontWeight: '600', color: '#059669' }}>{formatCurrency(r.amount, group?.currency)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Declined */}
                      {loan.status === 'declined' && (
                        <div style={{ textAlign: 'center', padding: '16px', color: '#991B1B' }}>
                          <span style={{ fontSize: '24px' }}>✗</span>
                          <p style={{ margin: '8px 0 0' }}>You declined this loan offer.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Loan Request Modal */}
          {showLoanRequestModal && (
            <div className="modal-overlay" onClick={() => setShowLoanRequestModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
                <div className="modal-header">
                  <h2>🏦 Request a Loan</h2>
                  <button className="close-btn" onClick={() => setShowLoanRequestModal(false)}>×</button>
                </div>
                <div className="modal-body">
                  <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
                    Submit a loan request. Leadership will review and set the full terms, which you can then accept or decline.
                  </p>
                  <div className="form-group">
                    <label>Loan Amount ({getCurrencySymbol(group?.currency)})</label>
                    <input
                      type="number"
                      value={loanRequestForm.amount}
                      onChange={e => setLoanRequestForm({ ...loanRequestForm, amount: e.target.value })}
                      placeholder="Enter amount"
                      data-testid="loan-amount-input"
                      min="1"
                      style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Purpose</label>
                    <textarea
                      value={loanRequestForm.purpose}
                      onChange={e => setLoanRequestForm({ ...loanRequestForm, purpose: e.target.value })}
                      placeholder="What is the loan for?"
                      data-testid="loan-purpose-input"
                      rows={3}
                      style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', resize: 'vertical' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Preferred Term (months)</label>
                    <input
                      type="number"
                      value={loanRequestForm.term_months}
                      onChange={e => setLoanRequestForm({ ...loanRequestForm, term_months: e.target.value })}
                      placeholder="e.g. 12"
                      data-testid="loan-term-input"
                      min="1"
                      style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Physical Address</label>
                    <input
                      type="text"
                      value={loanRequestForm.physical_address}
                      onChange={e => setLoanRequestForm({ ...loanRequestForm, physical_address: e.target.value })}
                      placeholder="Enter physical address"
                      data-testid="loan-physical-address-input"
                      style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={loanRequestForm.sameAddress}
                        onChange={e => setLoanRequestForm({
                          ...loanRequestForm,
                          sameAddress: e.target.checked,
                          mailing_address: e.target.checked ? loanRequestForm.physical_address : loanRequestForm.mailing_address
                        })}
                        data-testid="same-address-checkbox"
                      />
                      <span style={{ fontSize: '14px' }}>Mailing address same as physical address</span>
                    </label>
                  </div>
                  {!loanRequestForm.sameAddress && (
                    <div className="form-group">
                      <label>Mailing Address</label>
                      <input
                        type="text"
                        value={loanRequestForm.mailing_address}
                        onChange={e => setLoanRequestForm({ ...loanRequestForm, mailing_address: e.target.value })}
                        placeholder="Enter mailing address"
                        data-testid="loan-mailing-address-input"
                        style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}
                      />
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
                    <button className="btn btn-secondary" onClick={() => setShowLoanRequestModal(false)}>Cancel</button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmitLoanRequest}
                      disabled={submittingLoanRequest}
                      data-testid="submit-loan-request-btn"
                    >
                      {submittingLoanRequest ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Proof of Payment Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content upload-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: isPoolPayment ? '600px' : '500px' }}>
            <div className="modal-header">
              <h2>{isPoolPayment ? '👥 Pool Payment Upload' : '📤 Upload Proof of Payment'}</h2>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Pool Payment Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: isPoolPayment ? '#EFF6FF' : '#F9FAFB', borderRadius: '10px', marginBottom: '16px', border: isPoolPayment ? '1px solid #BFDBFE' : '1px solid #E5E7EB' }}>
                <div>
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#1F2937' }}>Submit for Multiple Members</span>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>Toggle on if this payment covers other group members</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPoolPayment(!isPoolPayment)}
                  data-testid="pool-payment-toggle"
                  style={{
                    width: '48px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer', position: 'relative',
                    background: isPoolPayment ? '#3B82F6' : '#D1D5DB', transition: 'background 0.2s'
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '3px', width: '20px', height: '20px', borderRadius: '50%', background: 'white', transition: 'left 0.2s',
                    left: isPoolPayment ? '25px' : '3px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>

              <p className="upload-info">
                Upload an image or PDF of your payment receipt, transaction confirmation, or bank transfer proof.
              </p>
              
              {paymentNotifications.length > 0 && (
                <div className="form-group case-number-highlight">
                  <label>📋 Select Payment Notice Case Number {(paymentNotifications.length > 0 || isPoolPayment) ? '*' : '(Optional)'}</label>
                  <select 
                    value={uploadCaseNumber} 
                    onChange={(e) => setUploadCaseNumber(e.target.value)}
                    data-testid="upload-case-select"
                    className={!uploadCaseNumber && paymentNotifications.length > 0 ? 'highlight-required' : ''}
                  >
                    <option value="">-- Select the case number for this payment --</option>
                    {paymentNotifications.map((notif) => notif.case_number && (
                      <option key={notif.case_number} value={notif.case_number}>
                        Case #{notif.case_number} - {new Date(notif.created_at).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    Select which payment notification this proof is for.
                  </small>
                </div>
              )}

              {paymentNotifications.length === 0 && (
                <div className="form-group">
                  <label>Case Number {isPoolPayment ? '*' : '(Optional)'}</label>
                  <input 
                    type="text"
                    value={uploadCaseNumber} 
                    onChange={(e) => setUploadCaseNumber(e.target.value)}
                    placeholder="Enter case number if known"
                    data-testid="upload-case-input"
                  />
                </div>
              )}

              {/* Pool Payment Fields */}
              {isPoolPayment && (
                <div style={{ background: '#F0F9FF', padding: '16px', borderRadius: '10px', marginBottom: '12px', border: '1px solid #BAE6FD' }}>
                  <div className="form-group">
                    <label>Amount Shown in Proof *</label>
                    <input
                      type="number"
                      value={poolAmount}
                      onChange={(e) => setPoolAmount(e.target.value)}
                      placeholder="Enter total amount in the proof"
                      data-testid="pool-amount-input"
                      style={{ fontSize: '16px', fontWeight: '600' }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '8px' }}>
                    <label>Add Members by Membership Number</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={poolMemberInput}
                        onChange={(e) => setPoolMemberInput(e.target.value)}
                        placeholder="e.g. M-GRUP-001"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleLookupPoolMember())}
                        data-testid="pool-member-input"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={handleLookupPoolMember}
                        disabled={lookingUpMember || !poolMemberInput.trim()}
                        data-testid="pool-member-confirm-btn"
                        style={{
                          padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                          background: '#3B82F6', color: 'white', fontWeight: '600', fontSize: '13px',
                          opacity: (lookingUpMember || !poolMemberInput.trim()) ? 0.5 : 1
                        }}
                      >
                        {lookingUpMember ? 'Checking...' : 'Confirm'}
                      </button>
                    </div>
                    <small style={{ color: '#6B7280', fontSize: '12px' }}>Enter the membership number and click Confirm to verify the member</small>
                  </div>

                  {poolMembers.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px', display: 'block' }}>
                        Members Added ({poolMembers.length}):
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {poolMembers.map((pm) => (
                          <div key={pm.member_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'white', borderRadius: '8px', border: '1px solid #E5E7EB' }} data-testid={`pool-member-${pm.member_id}`}>
                            <div>
                              <span style={{ fontWeight: '500', fontSize: '13px', color: '#1F2937' }}>{pm.name}</span>
                              <span style={{ color: '#6B7280', marginLeft: '8px', fontSize: '12px' }}>{pm.membership_number}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removePoolMember(pm.member_id)}
                              style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                              data-testid={`remove-pool-member-${pm.member_id}`}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-group">
                <label>Select File *</label>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  data-testid="upload-file-input"
                />
                {uploadFile && (
                  <div className="selected-file-info">
                    <span className="file-icon">{uploadFile.type.includes('pdf') ? '📄' : '🖼️'}</span>
                    <span className="file-name">{uploadFile.name}</span>
                    <span className="file-size">({(uploadFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                )}
                <small className="form-help">Accepted formats: JPG, PNG, GIF, PDF (Max 10MB)</small>
              </div>
              
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={uploadNotes}
                  onChange={(e) => setUploadNotes(e.target.value)}
                  placeholder="Add any notes about this payment (e.g., transaction ID, date paid)..."
                  rows="3"
                  data-testid="upload-notes-input"
                />
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleUploadProof}
                  disabled={uploading || !uploadFile || (isPoolPayment && (poolMembers.length === 0 || !poolAmount))}
                  data-testid="submit-proof-btn"
                >
                  {uploading ? 'Uploading...' : (isPoolPayment ? `Upload Pool Payment (${poolMembers.length + 1} members)` : 'Upload Proof')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Top-Up Modal */}
      {showTopUpModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Top Up Prepaid Balance</h2>
              <button className="close-btn" onClick={() => setShowTopUpModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Add funds to your prepaid balance. This balance will be used to automatically pay for approved case notices.
              </p>
              
              <div className="current-balance-display">
                <span className="label">Current Balance:</span>
                <span className="value">{formatCurrency(wallet.wallet_balance || 0, group?.currency)}</span>
              </div>
              
              <div className="form-group">
                <label>Top-Up Amount ({getCurrencySymbol(group?.currency)})</label>
                <div className="amount-input-group">
                  {topUpAmount && <span className="currency-prefix">{getCurrencySymbol(group?.currency)}</span>}
                  <input
                    type="number"
                    min="5"
                    step="0.01"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    placeholder="Enter amount"
                    className={topUpAmount ? 'has-value' : ''}
                    data-testid="topup-amount-input"
                  />
                </div>
                <small className="form-help">Minimum top-up: {formatCurrency(5, group?.currency)}</small>
              </div>
              
              <div className="quick-amounts">
                <span className="quick-label">Quick select:</span>
                <button className="quick-btn" onClick={() => setTopUpAmount('25')}>{getCurrencySymbol(group?.currency)}25</button>
                <button className="quick-btn" onClick={() => setTopUpAmount('50')}>{getCurrencySymbol(group?.currency)}50</button>
                <button className="quick-btn" onClick={() => setTopUpAmount('100')}>{getCurrencySymbol(group?.currency)}100</button>
                <button className="quick-btn" onClick={() => setTopUpAmount(String((wallet.contribution_amount || 50) * 3))}>
                  3x Contribution
                </button>
              </div>
              
              <div className="topup-summary">
                <p>After top-up, your balance will be: <strong>{formatCurrency((wallet.wallet_balance || 0) + parseFloat(topUpAmount || 0), group?.currency)}</strong></p>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowTopUpModal(false);
                    setTopUpAmount('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleWalletTopUp}
                  disabled={!topUpAmount || parseFloat(topUpAmount) < 5}
                  data-testid="confirm-topup-btn"
                >
                  💳 Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* M-Pesa Payment Modal */}
      {showMpesaModal && (
        <div className="modal-overlay" onClick={() => !mpesaSending && setShowMpesaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header" style={{ background: '#4CAF50', color: 'white', borderRadius: '10px 10px 0 0', padding: '16px 20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>M-Pesa Payment</h3>
              <button className="close-btn" onClick={() => !mpesaSending && setShowMpesaModal(false)} style={{ color: 'white' }}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {mpesaStatus === 'completed' ? (
                <div data-testid="mpesa-success" style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#DEF7EC', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '28px' }}>
                    &#10003;
                  </div>
                  <h4 style={{ margin: '0 0 8px', color: '#065F46', fontSize: '18px' }}>Payment Confirmed</h4>
                  <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>Your M-Pesa payment has been received and recorded.</p>
                  <button onClick={() => setShowMpesaModal(false)} className="btn btn-primary" style={{ marginTop: '20px' }}>Done</button>
                </div>
              ) : (
                <>
                  <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#166534' }}>
                    An M-Pesa STK Push will be sent to your phone. Enter your PIN on the phone to complete the payment.
                  </div>

                  {paymentNotifications.filter(n => !n.is_paid).length > 1 && (
                    <div className="form-group" style={{ marginBottom: '14px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>Case Number *</label>
                      <select
                        value={mpesaCaseNumber}
                        onChange={(e) => setMpesaCaseNumber(e.target.value)}
                        data-testid="mpesa-case-select"
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                      >
                        <option value="">-- Select case --</option>
                        {paymentNotifications.filter(n => !n.is_paid).map(n => (
                          <option key={n.case_number} value={n.case_number}>Case #{n.case_number}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '4px' }}>M-Pesa Phone Number *</label>
                    <input
                      type="tel"
                      value={mpesaPhone}
                      onChange={(e) => setMpesaPhone(e.target.value.replace(/[^0-9+]/g, ''))}
                      placeholder="e.g. 0712345678"
                      data-testid="mpesa-phone-input"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '14px' }}
                    />
                    <small style={{ fontSize: '11px', color: '#9CA3AF' }}>Safaricom number registered with M-Pesa</small>
                  </div>

                  <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: '#6B7280' }}>Amount:</span>
                      <span style={{ fontWeight: 700, color: '#1F2937' }}>{formatCurrency(group?.payment_amount, group?.currency)}</span>
                    </div>
                    {mpesaCaseNumber && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '6px' }}>
                        <span style={{ color: '#6B7280' }}>Case:</span>
                        <span style={{ color: '#374151' }}>#{mpesaCaseNumber}</span>
                      </div>
                    )}
                  </div>

                  {mpesaStatus === 'sent' && (
                    <div data-testid="mpesa-waiting" style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400E', marginBottom: '4px' }}>Waiting for payment...</div>
                      <div style={{ fontSize: '12px', color: '#B45309' }}>Check your phone and enter your M-Pesa PIN</div>
                      <div style={{ marginTop: '8px' }}>
                        <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B', animation: 'pulse 1s infinite' }} />
                      </div>
                      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
                    </div>
                  )}

                  {mpesaStatus === 'failed' && (
                    <div data-testid="mpesa-failed" style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'center', fontSize: '13px', color: '#991B1B' }}>
                      Payment failed or was cancelled. You can try again.
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setShowMpesaModal(false)}
                      className="btn btn-secondary"
                      disabled={mpesaSending}
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleMpesaPayment}
                      disabled={mpesaSending || mpesaStatus === 'sent' || !mpesaPhone || !mpesaCaseNumber}
                      data-testid="mpesa-send-btn"
                      style={{ flex: 1, background: '#4CAF50', color: 'white', border: 'none', borderRadius: '6px', padding: '10px', fontWeight: 600, cursor: mpesaSending ? 'wait' : 'pointer', opacity: (mpesaSending || mpesaStatus === 'sent') ? 0.6 : 1 }}
                    >
                      {mpesaSending ? 'Sending...' : mpesaStatus === 'sent' ? 'Awaiting Payment...' : 'Send M-Pesa Request'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Button */}
      <button 
        className="help-button"
        onClick={() => setShowHelpGuide(true)}
        data-testid="member-help-button"
        title="Help Guide"
      >
        <span>?</span> Help Guide
      </button>

      {/* Help Guide Modal */}
      {showHelpGuide && (
        <MemberHelpGuide onClose={() => setShowHelpGuide(false)} />
      )}
    </div>
  );
}